<template>
  <div
    id="ticketDetail"
    :data-view-ready="!loadingAnimation && (!!ticket || ticketDeleted === true)">
    <div
      v-if="ticketDeleted === true"
      class="row">
      <h1 class="col-12 mt-3 mb-3 pl-0 text-center text-lg">
        {{ $t('message.loadingErrors.ticketDeleted') }}
      </h1>
    </div>
    <div
      v-else-if="ticketClosed === true"
      class="row">
      <h1 class="col-12 mt-3 mb-3 pl-0 text-center text-lg">
        {{ $t('message.loadingErrors.ticketClosed', { ordernumber: ticket ? ticket.key : '' }) }}
      </h1>
    </div>
    <section
      v-else>
      <div
        v-if="loadingAnimation"
        class="row">
        <div class="col text-center">
          <coozzy-spinner class="m-3" />
        </div>
      </div>
      <div
        v-else
        class="row">
        <div class="col-12 mb-2">
          <coozzy-page-title
            v-if="ticket"
            :title="(ticket.key ? ticket.key : '') + (ticket.title !== '' ? (' - ' + ticket.title) : '')"
            :action-text="!publicTicket && !orderTicket ? String($t('message.generic.back')) : ''"
            :from="'ticketDetails'"
            action-button-design="secondary"
            @click="backClicked">
            <template #before-title-slot>
              <img
                v-b-tooltip="{placement : 'left', title : $t('message.ticketOverview.ticket.type.' + ticket.type)}"
                height="20px"
                class="mr-1 mb-1"
                :src="`/img/ticket/${ticket.type?.split('TICKET_TYPE_')[1].toLowerCase()}.svg`"
                alt="type"> {{ $t('message.ticketOverview.ticket.type.' + ticket.type) }}
            </template>
          </coozzy-page-title>
        </div>
      </div>
      <div
        v-if="ticket && ticket.invoiceDocumentId && isFileTransmissionsProcessed"
        class="row">
        <div class="col-12 mb-2">
          <coozzy-success-icon
            :title="$t('message.export.successful')" /> {{ $t('message.ticketOverview.ticket.uploadInvoice.statusInvoice') }}
        </div>
      </div>
      <document
        v-if="ticket && ticket.invoiceDocumentId && invoiceDocument"
        :key="invoiceDocument.id"
        :document="invoiceDocument"
        :is-invoice="true"
        :public-ticket="publicTicket"
        :process-button-disabled="isFileTransmissionsProcessed || invoiceProcessing"
        :is-owner-deep-o-enabled="isOwnerDeepOEnabled"
        :is-deep-o-enabled="isDeepOEnabled || isFileTransmissionsProcessed"
        @process-invoice="startProcessingInvoice"
        @version-added="versionAdded"
        @delete-document="deleteDocument" />
      <coozzy-card v-if="ticket">
        <div class="row">
          <div class="col-12 col-md-7">
            <div class="row">
              <h6
                v-if="publicTicket"
                class="mb-3 col-12 position-relative">
                <img
                  class="logo_company mb-2"
                  :src="publicTicket.creator.accountLogoUrl"
                  alt="Logo">
                <label class="d-block">{{ publicTicket.creator.accountName }}</label>
                <address-text :address="publicTicket.creator.address" />
              </h6>
            </div>
            <div class="row mb-2">
              <div
                v-if="ticket.type !== 'TICKET_TYPE_CHECKLIST'"
                class="col">
                <label for="title">{{ $t('message.ticketOverview.ticket.description') }}</label>
                <!-- eslint-disable vue/no-v-html -->
                <p
                  class="pre-wrap mb-1"
                  v-html="ticket.description" />
                <!-- eslint-enable vue/no-v-html -->
              </div>
              <div
                v-else
                class="col-12">
                <label for="title">{{ $t('message.ticketOverview.ticket.todo') }}</label>
                <div
                  v-if="listTasks.length > 0"
                  class="col-12 mt-2">
                  <div
                    v-for="task in listTasks"
                    :key="`task-${task.id}`"
                    class="row item-task">
                    <div class="col-md-12 col-lg-6 col-xl-7">
                      <div class="row">
                        <div class="col-7 align-content-center">
                          <span>
                            {{ task.title }}
                          </span>
                        </div>
                        <div class="col-5 p-0 align-content-center">
                          <template v-if="!editingTaskNote || (editingTaskNote && editingTaskNote.id !== task.id)">
                            <coozzy-button
                              v-if="task.note !== ''"
                              v-b-tooltip.hover.html="task.note"
                              data-cy="ticket-checklist-edit-note-btn"
                              design="transparent"
                              class="p-0 mb-1 ml-2 float-right"
                              @click="editTaskNote(task)">
                              <coozzy-comment-icon-img />
                            </coozzy-button>
                            <coozzy-button
                              v-else
                              v-b-tooltip.hover.html="task.note"
                              data-cy="ticket-checklist-add-note-btn"
                              design="transparent"
                              class="border float-right"
                              @click="editTaskNote(task)">
                              {{ $t('message.ticketOverview.ticket.addNote') }}
                            </coozzy-button>
                          </template>
                          <div
                            v-if="editingTaskNote && editingTaskNote.id === task.id"
                            class="container_textArea">
                            <coozzy-form-textarea
                              v-model="task.note"
                              :initial="task.note"
                              data-cy="ticket-checklist-note-textarea"
                              class="w-100"
                              autofocus
                              :placeholder="$t('message.ticketOverview.ticket.writeNote')"
                              :rows="3"
                              :show-label="false"
                              :text="task.note"
                              :value-text-area="task.note"
                              @keydown.enter.prevent="saveTaskNote" />
                            <coozzy-button
                              class="icon"
                              data-cy="ticket-checklist-save-note-btn"
                              design="primary"
                              @click="saveTaskNote(null)">
                              <coozzy-enter-icon
                                name="enterIcon"
                                :active="true" />
                            </coozzy-button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="col-md-12 col-lg-6 col-xl-5">
                      <div class="row">
                        <div class="col-7">
                          <coozzy-button
                            data-cy="ticket-checklist-assignee-btn"
                            design="transparent"
                            class="border w-100"
                            @click="showEditChecklistAssigneeModal(task)">
                            <span
                              v-if="task.assignee && task.assignee.profile"
                              class="w-100 m-0 float-right text-center">
                              <span>{{ task.assignee.profile.firstName + ' ' + task.assignee.profile.lastName }}</span>
                            </span>
                            <span v-else> - </span>
                          </coozzy-button>
                        </div>
                        <div class="col-5 checklist-row p-0">
                          <coozzy-form-select
                            v-model="task.status"
                            data-cy="ticket-checklist-select-status"
                            :class="`status-design ${(task.status.split('CHECKLIST_ITEM_STATUS_')[1]).toLowerCase()}`"
                            @change="saveTaskStatus(task)">
                            <option
                              v-for="status in availableStatusChecklist"
                              :key="status"
                              :value="status">
                              {{ $t('message.ticketOverview.checklistStatus.' + status) }}
                            </option>
                          </coozzy-form-select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- Attachments -->
            <div
              v-if="(orderTicket && orderTicket.listAttachment && orderTicket.listAttachment.length > 0) || filteredMediaList.length > 0"
              class="row mb-2">
              <div class="col">
                <label for="attachment"> {{ $t('message.ticketOverview.ticket.attachment') }}
                  ({{ filteredMediaList.length + (orderTicket?.listAttachment?.length || 0) }})</label>
                <div
                  v-if="filteredMediaList.length > 0 || (orderTicket && orderTicket.listAttachment && orderTicket.listAttachment.length > 0)"
                  class="row m-0">
                  <div class="col-12 p-0">
                    <div
                      v-if="countDocs > 0 || (orderTicket && orderTicket.listAttachment && orderTicket.listAttachment.length > 0)"
                      class="row m-0 mb-2">
                      <label
                        class="col-12 p-0 subtitle"> {{ $t('message.ticketOverview.attachment.document') }}
                        ({{ countDocs + (orderTicket?.listAttachment?.length || 0) }})</label>
                      <template
                        v-if="countDocs > 0">
                        <div
                          v-for="media in filteredMediaList"
                          :key="media.media ? media.media.id : media.id"
                          class="col-12 pb-1 px-0">
                          <template
                            v-if="!(['image/jpeg', 'image/png', 'image/gif', 'video/webm', 'video/mp4'].indexOf(media.media ? media.media.mimeType : media.mimeType) !== -1)">
                            <div
                              :key="media.id"
                              class="border mr-1">
                              <a
                                class="cardDocs"
                                :href="media.media ? media.media.url : media.url"
                                target="_blank"
                                :download="media.media ? media.media.filename : media.filename">
                                <div
                                  class="row m-0 d-flex">
                                  <div
                                    class="col-auto iconFile d-flex align-items-center"
                                    @click="downloadFiles(media, $event)">
                                    <coozzy-file-download-icon />
                                  </div>
                                  <div
                                    class="col-11 pl-0 pr-1">
                                    <p class="m-0 docName">
                                      <span class="link">{{
                                        media.media ? media.media.filename : media.filename || 'unknown_filename'
                                      }}</span>
                                    </p>
                                    <p class="m-0 docSize">
                                      <span class="link">{{ Math.floor((media.size) / 1024) + ' KB' }}</span>
                                    </p>
                                  </div>
                                </div>
                              </a>
                            </div>
                          </template>
                        </div>
                      </template>
                      <template
                        v-if="orderTicket && orderTicket.listAttachment && orderTicket.listAttachment.length > 0">
                        <div
                          v-for="media in orderTicket.listAttachment"
                          :key="media.media ? media.media.id : media.id"
                          class="col-12 col-md-12 pb-1 px-0">
                          <template
                            v-if="!(['image/jpeg', 'image/png', 'image/gif'].indexOf(media.media ? media.media.mimeType : media.mimeType) !== -1)">
                            <div
                              :key="media.id"
                              class="border mr-1">
                              <a
                                class="cardDocs"
                                @click="documentPreview(media.url, media.renderId)">
                                <div
                                  class="row m-0 d-flex">
                                  <div
                                    class="col-auto iconFile d-flex align-items-center">
                                    <div
                                      v-if="media.loading"
                                      class="text-center custom-spin">
                                      <coozzy-spinner />
                                    </div>
                                    <div v-else>
                                      <coozzy-file-download-icon />
                                    </div>
                                  </div>
                                  <div
                                    class="col-11 pl-0 pr-1">
                                    <p class="m-0 docName">
                                      <span class="link">{{
                                        media.media ? media.media.filename : media.filename || 'unknown_filename'
                                      }}</span>
                                    </p>
                                    <p class="m-0 docSize">
                                      <span class="link">{{
                                        media.size ? (Math.floor((media.size) / 1024) + ' KB') : '- KB'
                                      }}</span>
                                    </p>
                                  </div>
                                </div>
                              </a>
                            </div>
                          </template>
                        </div>
                      </template>
                    </div>
                    <div
                      v-if="countImg > 0"
                      class="row m-0 mb-2">
                      <label class="col-12 p-0 subtitle"> {{ $t('message.ticketOverview.attachment.image') }}
                        ({{ countImg }})</label>
                      <template
                        v-for="media in filteredMediaList">
                        <div
                          v-if="['image/jpeg', 'image/png', 'image/gif'].indexOf(media.mimeType) !== -1 && media.url"
                          :key="media.id"
                          class="media-block col-12 col-md-2 square">
                          <div
                            class="uploaded-file image cursor-pointer w-100"
                            :style="{ 'background-image': 'url(' + media.url + ')' }"
                            @click="imageClicked(media)" />
                        </div>
                      </template>
                    </div>
                    <div
                      v-if="countVideo > 0"
                      class="row m-0 mb-2">
                      <label class="col-12 p-0 subtitle"> {{ $t('message.ticketOverview.attachment.video') }}
                        ({{ countVideo }})</label>
                      <template
                        v-for="media in filteredMediaList">
                        <div
                          v-if="['video/webm', 'video/mp4'].indexOf(media.mimeType) !== -1"
                          :key="media.id"
                          class="media-block col-12 col-md-2 square">
                          <coozzy-thumbnail
                            v-if="getMediaUrl(media) !== null"
                            :key="media.id"
                            :video-src="getMediaUrl(media)"
                            :video-url="media.url"
                            class="uploaded-file cursor-pointer"
                            @imageClicked="openVideo(media)" />
                        </div>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- Linked tickets -->
            <div
              v-if="(ticket.links && ticket.links.length > 0) || showLinkInputs"
              id="linkedTickets"
              class="row mb-2">
              <div class="col">
                <label for="linkedTickets">{{
                  showLinkInputs ? $t('message.ticketOverview.ticket.linkTickets') : $t('message.ticketOverview.ticket.linkedTickets')
                }}</label>
                <div
                  v-if="showLinkInputs && (isEditor || isTicketEditor)"
                  class="row">
                  <div class="col-3 mb-2">
                    <coozzy-form-select
                      v-model="typeOfLink"
                      data-cy="ticket-link-type-select">
                      <option
                        v-for="item in availableLinkTypes"
                        :key="item"
                        :value="item">
                        {{ $t('message.ticketOverview.ticket.linkType.' + item) }}
                      </option>
                    </coozzy-form-select>
                  </div>
                  <div class="col-9">
                    <vue-autosuggest
                      :key="refreshAutosuggestKey"
                      v-model="searchForTickets"
                      :suggestions="resultTicketsForLink"
                      :input-props="inputProps"
                      :get-suggestion-value="getSuggestionValue"
                      @item-changed="itemChanged"
                      @input="getTickets()"
                      @selected="onSelectLinked">
                      <template #default="{suggestion}">
                        <span
                          v-for="st in autosuggestTicketRows(suggestion)"
                          :key="st.id"
                          class="my-suggestion-item"
                          :class="checkIsDisabled(st) ? 'disableOptionHover' : ''">
                          <img
                            height="20px"
                            class="mr-1 mb-1"
                            :src="`/img/ticket/${st.type.split('TICKET_TYPE_')[1].toLowerCase()}.svg`"
                            alt="type">{{ st.key }} {{ st.title }}</span>
                      </template>
                    </vue-autosuggest>
                  </div>
                  <div class="col">
                    <coozzy-button
                      size="small"
                      data-cy="ticket-link-toggle-btn"
                      class="border float-right mb-0 ml-2"
                      design="transparent"
                      @click="showLinkInputs = !showLinkInputs">
                      {{ $t('message.generic.cancel') }}
                    </coozzy-button>
                    <coozzy-button
                      size="small"
                      data-cy="ticket-link-save-btn"
                      class="float-right mb-0"
                      design="green"
                      :disabled="ticketToLink?.length === 0 || searchForTickets?.length === 0"
                      @click="updateTicket('link')">
                      {{ $t('message.ticketOverview.ticket.link') }}
                    </coozzy-button>
                  </div>
                </div>
                <template v-if="!showLinkInputs">
                  <p
                    v-if="filteredTicketLinked('TICKET_LINK_TYPE_BLOCKS').length === 0 && filteredTicketLinked('TICKET_LINK_TYPE_RELATES').length === 0 && filteredTicketLinked('TICKET_LINK_TYPE_IS_BLOCKED_BY').length === 0"
                    class="mb-1">
                    -
                  </p>
                </template>
                <template v-if="filteredTicketLinked('TICKET_LINK_TYPE_BLOCKS').length > 0">
                  <div class="row">
                    <div class="col">
                      <label
                        for="blocks"
                        class="subtitle">
                        {{ $t('message.ticketOverview.ticket.linkType.TICKET_LINK_TYPE_BLOCKS') }}
                      </label>
                      <template
                        v-for="item in filteredTicketLinked('TICKET_LINK_TYPE_BLOCKS')">
                        <div
                          v-if="listTickets.find(x => x.id === item.ticketId)"
                          :key="'TICKET_LINK_TYPE_BLOCKS' + item.ticketId"
                          class="row mx-0 mb-2 p-1 linked-row">
                          <div class="col-8 p-0 label">
                            <router-link
                              :to="{ name: getTicketDetailRoute(listTickets.find(x => x.id === item.ticketId)).name, params: getTicketDetailRoute(listTickets.find(x => x.id === item.ticketId)).params }"
                              @click.native.capture="onLinkedTicketClick($event, listTickets.find(x => x.id === item.ticketId))">
                              <img
                                v-b-tooltip="{placement : 'left', title : $t('message.ticketOverview.ticket.type.' + listTickets.find(x => x.id === item.ticketId).type)}"
                                height="20px"
                                class="mr-1"
                                :src="`/img/ticket/${listTickets.find(x => x.id === item.ticketId).type.split('TICKET_TYPE_')[1].toLowerCase()}.svg`"
                                alt="type"> {{ listTickets.find(x => x.id === item.ticketId).key }}
                              {{ listTickets.find(x => x.id === item.ticketId).title }}
                            </router-link>
                          </div>
                          <div
                            v-if="!publicTicket"
                            class="col d-inline pr-1">
                            <coozzy-button
                              v-if="isEditor || isTicketEditor"
                              size="small"
                              data-cy="ticket-link-remove-btn"
                              class="remove-linked-btn float-right mb-0 ml-2"
                              design="transparent"
                              @click="removeTicketLinkedClicked(item)">
                              <coozzy-close-icon />
                            </coozzy-button>
                            <div
                              :class="`d-inline float-right status-design ${(listTickets.find(x => x.id === item.ticketId).status.split('TICKET_STATUS_')[1]).toLowerCase()}`">
                              {{
                                $t('message.ticketOverview.status.' + listTickets.find(x => x.id === item.ticketId).status)
                              }}
                            </div>
                            <img
                              v-b-tooltip="{container: '#linkedTickets', title: listTickets.find(x => x.id === item.ticketId).assignee ? listTickets.find(x => x.id === item.ticketId).assignee.name : ''}"
                              class="align-self-center float-right avatar mr-2"
                              :src="listTickets.find(x => x.id === item.ticketId).assignee ? listTickets.find(x => x.id === item.ticketId).assignee.profilePictureUrl ? listTickets.find(x => x.id === item.ticketId).assignee.profilePictureUrl : '/avatar.png' : '/avatar.png'"
                              alt="assignee">
                            <img
                              height="20px"
                              class="mr-2 float-right ml-2"
                              :src="`/img/priority/prio-${listTickets.find(x => x.id === item.ticketId).priority}.svg`"
                              alt="priority">
                          </div>
                          <div
                            v-if="publicTicket"
                            class="col d-inline pr-1">
                            <coozzy-button
                              v-if="isEditor || isTicketEditor"
                              size="small"
                              data-cy="ticket-link-remove-btn"
                              class="border float-right mb-0 ml-2"
                              design="transparent"
                              @click="removeTicketLinkedClicked(item)">
                              <coozzy-delete-icon />
                            </coozzy-button>
                            <div
                              :class="`d-inline float-right status-design ${(listTickets.find(x => x.id === item.ticketId).status.split('TICKET_STATUS_')[1]).toLowerCase()}`">
                              {{
                                $t('message.ticketOverview.status.' + listTickets.find(x => x.id === item.ticketId).status)
                              }}
                            </div>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>
                </template>
                <template v-if="filteredTicketLinked('TICKET_LINK_TYPE_RELATES').length > 0">
                  <div class="row">
                    <div class="col">
                      <label
                        for="blocks"
                        class="subtitle">
                        {{ $t('message.ticketOverview.ticket.linkType.TICKET_LINK_TYPE_RELATES') }}
                      </label>
                      <template
                        v-for="item in filteredTicketLinked('TICKET_LINK_TYPE_RELATES')">
                        <div
                          v-if="listTickets.find(x => x.id === item.ticketId)"
                          :key="'TICKET_LINK_TYPE_RELATES' + item.ticketId"
                          class="row mx-0 mb-2 p-1 linked-row">
                          <div class="col-8 p-0 label">
                            <router-link
                              :to="{ name: getTicketDetailRoute(listTickets.find(x => x.id === item.ticketId)).name, params: getTicketDetailRoute(listTickets.find(x => x.id === item.ticketId)).params }"
                              @click.native.capture="onLinkedTicketClick($event, listTickets.find(x => x.id === item.ticketId))">
                              <img
                                v-b-tooltip="{placement : 'left', title : $t('message.ticketOverview.ticket.type.' + listTickets.find(x => x.id === item.ticketId).type)}"
                                :src="`/img/ticket/${listTickets.find(x => x.id === item.ticketId).type.split('TICKET_TYPE_')[1].toLowerCase()}.svg`"
                                alt="type"
                                class="mr-1"
                                height="20px">
                              {{
                                listTickets.find(x => x.id === item.ticketId).key + ' ' + listTickets.find(x => x.id === item.ticketId).title
                              }}
                            </router-link>
                          </div>
                          <div
                            v-if="!publicTicket"
                            class="col d-inline pr-1">
                            <coozzy-button
                              v-if="isEditor || isTicketEditor"
                              size="small"
                              data-cy="ticket-link-remove-btn"
                              class="remove-linked-btn float-right mb-0 ml-2"
                              design="transparent"
                              @click="removeTicketLinkedClicked(item)">
                              <coozzy-close-icon />
                            </coozzy-button>
                            <div
                              :class="`d-inline float-right status-design ${(listTickets.find(x => x.id === item.ticketId).status.split('TICKET_STATUS_')[1]).toLowerCase()}`">
                              {{
                                $t('message.ticketOverview.status.' + listTickets.find(x => x.id === item.ticketId).status)
                              }}
                            </div>
                            <img
                              v-b-tooltip="{container: '#linkedTickets', title: listTickets.find(x => x.id === item.ticketId).assignee ? listTickets.find(x => x.id === item.ticketId).assignee.name : ''}"
                              class="align-self-center float-right avatar mr-2"
                              :src="listTickets.find(x => x.id === item.ticketId).assignee ? listTickets.find(x => x.id === item.ticketId).assignee.profilePictureUrl ? listTickets.find(x => x.id === item.ticketId).assignee.profilePictureUrl : '/avatar.png' : '/avatar.png'"
                              alt="assignee">
                            <img
                              height="20px"
                              class="mr-2 float-right ml-2"
                              :src="`/img/priority/prio-${listTickets.find(x => x.id === item.ticketId).priority}.svg`"
                              alt="priority">
                          </div>
                          <div
                            v-if="publicTicket"
                            class="col d-inline pr-1">
                            <coozzy-button
                              v-if="publicTicket && (isEditor || isTicketEditor)"
                              size="small"
                              data-cy="ticket-link-remove-btn"
                              class="border float-right mb-0 ml-2"
                              design="transparent"
                              @click="removeTicketLinkedClicked(item)">
                              <coozzy-delete-icon />
                            </coozzy-button>
                            <div
                              :class="`d-inline float-right status-design ${(listTickets.find(x => x.id === item.ticketId).status.split('TICKET_STATUS_')[1]).toLowerCase()}`">
                              {{
                                $t('message.ticketOverview.status.' + listTickets.find(x => x.id === item.ticketId).status)
                              }}
                            </div>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>
                </template>
                <template v-if="filteredTicketLinked('TICKET_LINK_TYPE_IS_BLOCKED_BY').length > 0">
                  <div class="row">
                    <div class="col">
                      <label
                        for="blocks"
                        class="subtitle">
                        {{ $t('message.ticketOverview.ticket.linkType.TICKET_LINK_TYPE_IS_BLOCKED_BY') }}
                      </label>
                      <template
                        v-for="item in filteredTicketLinked('TICKET_LINK_TYPE_IS_BLOCKED_BY')">
                        <div
                          v-if="listTickets.find(x => x.id === item.ticketId)"
                          :key="'TICKET_LINK_TYPE_IS_BLOCKED_BY' + item.ticketId"
                          class="row mx-0 mb-2 p-1 linked-row">
                          <div class="col-8 p-0 label">
                            <router-link
                              :to="{ name: getTicketDetailRoute(listTickets.find(x => x.id === item.ticketId)).name, params: getTicketDetailRoute(listTickets.find(x => x.id === item.ticketId)).params }"
                              @click.native.capture="onLinkedTicketClick($event, listTickets.find(x => x.id === item.ticketId))">
                              <img
                                v-b-tooltip="{placement : 'left', title : $t('message.ticketOverview.ticket.type.' + listTickets.find(x => x.id === item.ticketId).type)}"
                                height="20px"
                                class="mr-1"
                                :src="`/img/ticket/${listTickets.find(x => x.id === item.ticketId).type.split('TICKET_TYPE_')[1].toLowerCase()}.svg`"
                                alt="type"> {{ listTickets.find(x => x.id === item.ticketId).key }}
                              {{ listTickets.find(x => x.id === item.ticketId).title }}
                            </router-link>
                          </div>
                          <div
                            v-if="!publicTicket"
                            class="col d-inline pr-1">
                            <coozzy-button
                              v-if="isEditor || isTicketEditor"
                              size="small"
                              data-cy="ticket-link-remove-btn"
                              class="remove-linked-btn float-right mb-0 ml-2"
                              design="transparent"
                              @click="removeTicketLinkedClicked(item)">
                              <coozzy-close-icon />
                            </coozzy-button>
                            <div
                              :class="`d-inline float-right status-design ${(listTickets.find(x => x.id === item.ticketId).status.split('TICKET_STATUS_')[1]).toLowerCase()}`">
                              {{
                                $t('message.ticketOverview.status.' + listTickets.find(x => x.id === item.ticketId).status)
                              }}
                            </div>
                            <img
                              v-b-tooltip="{container: '#linkedTickets', title: listTickets.find(x => x.id === item.ticketId).assignee ? listTickets.find(x => x.id === item.ticketId).assignee.name : ''}"
                              class="align-self-center float-right avatar mr-2"
                              :src="listTickets.find(x => x.id === item.ticketId).assignee ? listTickets.find(x => x.id === item.ticketId).assignee.profilePictureUrl ? listTickets.find(x => x.id === item.ticketId).assignee.profilePictureUrl : '/avatar.png' : '/avatar.png'"
                              alt="assignee">
                            <img
                              height="20px"
                              class="mr-2 float-right ml-2"
                              :src="`/img/priority/prio-${listTickets.find(x => x.id === item.ticketId).priority}.svg`"
                              alt="priority">
                          </div>
                          <div
                            v-if="publicTicket"
                            class="col d-inline pr-1">
                            <coozzy-button
                              v-if="publicTicket && (isEditor || isTicketEditor)"
                              size="small"
                              data-cy="ticket-link-remove-btn"
                              class="border float-right mb-0 ml-2"
                              design="transparent"
                              @click="removeTicketLinkedClicked(item)">
                              <coozzy-delete-icon />
                            </coozzy-button>
                            <div
                              :class="`d-inline float-right status-design ${(listTickets.find(x => x.id === item.ticketId).status.split('TICKET_STATUS_')[1]).toLowerCase()}`">
                              {{
                                $t('message.ticketOverview.status.' + listTickets.find(x => x.id === item.ticketId).status)
                              }}
                            </div>
                          </div>
                        </div>
                      </template>
                    </div>
                  </div>
                </template>
              </div>
            </div>
            <b-tabs
              v-if="!orderTicket"
              v-model="tabIndex"
              class="mb-3 hrefSelector">
              <b-tab
                lazy>
                <template #title>
                  <h6
                    data-cy="ticket-tab-comments"
                    class="mb-0 title-card">
                    {{ $t('message.ticketOverview.ticket.tabs.comments') }}<span
                      class="text-blue"> | <b>{{ commentList.length }}</b></span>
                  </h6>
                </template>
                <comment
                  v-if="canAddComment"
                  ref="commentBlock"
                  :key="`comment-block`"
                  :reference-id="ticket.id"
                  :old-comment="comment"
                  :is-public="!!publicTicket"
                  :is-aic="isAicTicket"
                  :email="email"
                  :contact-list="contactList"
                  :employee-list="employeeList"
                  :ticket-type="ticket.type"
                  @comment-added="commentAdded"
                  @comment-changed="watchComment"
                  @comment-value="commentEdited" />
                <template v-if="commentList.length > 0">
                  <comment
                    v-for="item in (showAllComments ? commentList : commentList.slice(0,5))"
                    :key="`list-comment-block-${item.id}`"
                    :comment-item="item"
                    :read-only="true"
                    :contact-list="contactList"
                    :employee-list="employeeList"
                    @comment-updated="commentUpdated"
                    @comment-deleted="commentDeleted" />
                </template>
                <p
                  v-else
                  class="mt-2 mb-2 text-gray text-center">
                  {{ $t('message.ticketOverview.ticket.comment.emptyCommentsList') }}
                </p>
                <div
                  v-if="commentList.length > 5"
                  class="text-center borderTop">
                  <a
                    class="link"
                    @click="showAllComments = !showAllComments">
                    <template v-if="showAllComments === false">
                      {{ $t('message.ticketOverview.showAll') }}
                    </template>
                    <template v-else>
                      {{ $t('message.ticketOverview.hideAll') }}
                    </template>
                  </a>
                </div>
              </b-tab>
              <b-tab
                lazy>
                <template #title>
                  <h6
                    data-cy="ticket-tab-history"
                    class="mb-0 title-card">
                    {{ $t('message.ticketOverview.ticket.tabs.history') }}<span
                      class="text-blue"> | <b>{{ historyLength }}</b></span>
                  </h6>
                </template>
                <template>
                  <ticket-history
                    :ticket="ticket"
                    :list-event="history"
                    :list-links="historyLinkedTicket"
                    :list-references="historyReferences" />
                </template>
              </b-tab>
            </b-tabs>
          </div>
          <div class="col-12 col-md-5 order-first order-md-last mt-4">
            <div class="row mb-2 align-items-end">
              <div
                class="col flex-grow-0"
                :class="isAicTicket ? '' : !publicTicket && !orderTicket ? 'col-8' : 'col-12'"
                style="min-width: fit-content;">
                <div class="d-inline-block">
                  <label class="mr-2">{{ $t('message.ticketOverview.status.title') }}: </label>
                  <coozzy-form-select
                    v-model="ticket.status"
                    data-cy="ticket-select-status"
                    :class="`status-design ${(ticket.status.split('TICKET_STATUS_')[1]).toLowerCase()}`"
                    :disabled="orderTicket !== null || publicTicket !== null || !canEditStatus || isAileanAssigneeInternal"
                    @change="(value) => { handleTicketStatusChanged(value) }">
                    <option
                      v-for="status in availableStatus"
                      :key="status"
                      :value="status"
                      :disabled="publicTicket && (status === 'TICKET_STATUS_OPEN' || status === 'TICKET_STATUS_CLOSED' || (status === 'TICKET_STATUS_IN_PROGRESS' && ticket.status === 'TICKET_STATUS_DONE')) ">
                      {{ $t('message.ticketOverview.status.' + status) }}
                    </option>
                  </coozzy-form-select>
                </div>
                <div class="d-inline-block">
                  <template
                    v-if="ticketStatus === 'TICKET_STATUS_DONE' && publicTicket && isOwnerDeepOEnabled">
                    <coozzy-button
                      size="small"
                      data-cy="ticket-upload-invoice-btn"
                      class="ml-2 mb-0"
                      design="prop-green"
                      :disabled="invoiceDocument"
                      @click="finishOrderMethod">
                      {{ $t('message.ticketOverview.ticket.actions.uploadInvoice') }}
                    </coozzy-button>
                  </template>
                </div>
                <div
                  v-if="publicTicket && ticket.type === 'TICKET_TYPE_ORDER' && ['TICKET_STATUS_OPEN', 'TICKET_STATUS_IN_PROGRESS'].includes(ticket.status)"
                  class="d-inline-block">
                  <!--  Status Ticket Open -->
                  <template
                    v-if="ticket.status === 'TICKET_STATUS_OPEN'">
                    <coozzy-button
                      v-if="!declinedTicket"
                      size="small"
                      data-cy="ticket-accept-order-btn"
                      class="ml-2 mb-0"
                      design="prop-green"
                      @click="changePublicTicketStatus('TICKET_STATUS_IN_PROGRESS')">
                      {{ $t('message.ticketOverview.status.acceptOrder') }}
                    </coozzy-button>
                    <coozzy-button
                      size="small"
                      data-cy="ticket-decline-order-btn"
                      class="ml-2 mb-0"
                      design="red"
                      :disabled="declinedTicket"
                      @click="openModalDeclineTicket()">
                      {{ $t('message.ticketOverview.status.declineOrder') }}
                    </coozzy-button>
                  </template>
                  <template
                    v-if="ticketStatus === 'TICKET_STATUS_IN_PROGRESS'">
                    <coozzy-button
                      size="small"
                      data-cy="ticket-finish-order-btn"
                      class="ml-2 mb-0"
                      design="prop-green"
                      @click="finishOrderMethod">
                      {{ $t('message.ticketOverview.status.finishOrder') }}
                    </coozzy-button>
                  </template>
                </div>
              </div>
              <div
                v-if="isAicTicket"
                class="col flex-grow-0">
                <coozzy-button
                  v-if="!aicLoading"
                  data-cy="ticket-aic-btn"
                  style="padding-top: 3px; padding-bottom: 5px"
                  @click="showInAic">
                  <img
                    src="@/assets/integrations/aic.svg"
                    alt="AIC"
                    height="10px">
                </coozzy-button>
                <coozzy-spinner v-else />
              </div>
              <div
                v-if="!publicTicket && !orderTicket"
                class="col">
                <coozzy-dropdown
                  v-if="(isTicketingEnabled && (canEditTicket || (isAileanAssigneeInternal && hasEditRights))) || isTicketDeletor"
                  :text="String($t('message.generic.actions'))"
                  data-cy="ticket-actions-dropdown"
                  class="col-auto mr-0 pr-0 float-right"
                  design="light"
                  size="sm">
                  <coozzy-dropdown-item
                    v-if="hasEditRights && !isAicTicket"
                    data-cy="ticket-action-edit"
                    :disabled="!canEditTicket"
                    @click="editTicket">
                    {{ $t('message.generic.edit') }}
                  </coozzy-dropdown-item>
                  <coozzy-dropdown-item
                    v-if="ticket.type === 'TICKET_TYPE_ORDER'"
                    data-cy="ticket-action-upload-invoice"
                    :disabled="isFileTransmissionsProcessed || (publicTicket && !isDeepOEnabled) || !canEditTicket"
                    @click="showUploadInvoiceModal">
                    {{ invoiceDocument ? $t('message.ticketOverview.ticket.actions.reUploadInvoice') : $t('message.ticketOverview.ticket.actions.uploadInvoice') }}
                  </coozzy-dropdown-item>
                  <coozzy-dropdown-item
                    v-if="hasEditRights"
                    data-cy="ticket-action-mark-unread"
                    :disabled="!canEditTicket"
                    @click="markAsUnread">
                    {{ $t('message.ticketOverview.ticket.actions.markAsUnread') }}
                  </coozzy-dropdown-item>
                  <coozzy-dropdown-item
                    v-if="hasEditRights && ticket.type === 'TICKET_TYPE_ORDER'"
                    data-cy="ticket-action-change-operator"
                    :disabled="!canEditTicket"
                    @click="editTicket('changeOperator')">
                    {{ $t('message.ticketOverview.ticket.actions.changeOperator') }}
                  </coozzy-dropdown-item>
                  <coozzy-dropdown-item
                    v-if="hasEditRights && ticket.type === 'TICKET_TYPE_ORDER' && ticket.status === 'TICKET_STATUS_DONE' && referencesDevices.length > 0"
                    data-cy="ticket-action-change-condition"
                    :disabled="!canEditTicket"
                    @click="openChangeConditionModal">
                    {{ $t('message.ticketOverview.ticket.actions.changeCondition') }}
                  </coozzy-dropdown-item>
                  <coozzy-dropdown-item
                    v-if="hasEditRights && ticket.type === 'TICKET_TYPE_ORDER' && ticket.status === 'TICKET_STATUS_DONE' && referencesDevices.length === 1 && referencesDevices[0]"
                    data-cy="ticket-action-change-device"
                    :disabled="!canEditTicket"
                    @click="openChangeDeviceModal">
                    {{ $t('message.ticketOverview.ticket.actions.replaceComponent') }}
                  </coozzy-dropdown-item>
                  <coozzy-dropdown-item
                    v-if="hasEditRights"
                    data-cy="ticket-action-link"
                    :disabled="!canEditTicket"
                    @click="showLinkBlock">
                    {{ $t('message.ticketOverview.ticket.actions.link') }}
                  </coozzy-dropdown-item>
                  <coozzy-dropdown-item
                    v-if="isTicketingEnabled && (isCompanyAdmin || isEditor || isTicketEditor || isTicketCreator) && ticket.type === 'TICKET_TYPE_TASK' && !isOwnerModule && !isAssetModule"
                    data-cy="ticket-action-add-connections"
                    :disabled="!canEditTicket"
                    @click="showAddTicketConnectionsModal()">
                    {{ $t('message.createOrder.title') }}
                  </coozzy-dropdown-item>
                  <coozzy-dropdown-item
                    v-if="(isTicketDeletor || hasEditRights) && !isAicTicket"
                    data-cy="ticket-action-delete"
                    :disabled="isAileanAssigneeInternal || (!canEditTicket && !isTicketDeletor)"
                    @click="confirmDeleteTicket">
                    {{ $t('message.ticketOverview.ticket.actions.delete') }}
                  </coozzy-dropdown-item>
                  <coozzy-dropdown-item
                    v-if="ticket.type === 'TICKET_TYPE_TASK' && linkedOrders.length > 1"
                    data-cy="ticket-action-merge-orders"
                    :disabled="linkedOrders.length <= 1 || !canEditTicket"
                    @click="showMergeOrdersModal">
                    {{ $t('message.ticketOverview.ticket.actions.mergeOrders') }}
                  </coozzy-dropdown-item>
                  <coozzy-dropdown-item
                    v-if="aicEnabled && !isAicTicket"
                    data-cy="ticket-action-report-damage"
                    @click="showModalReportDamageInsurance">
                    {{ $t('message.ticketOverview.reportDamageInsurance') }}
                  </coozzy-dropdown-item>
                </coozzy-dropdown>
              </div>
            </div>
            <b-card>
              <div class="row mb-2">
                <div class="col-12 col-md-3">
                  <label v-if="!publicTicket && ticket.type !== 'TICKET_TYPE_TASK'">{{
                    $t('message.createOrder.watcher')
                  }}</label>
                  <br v-if="!publicTicket && ticket.type !== 'TICKET_TYPE_TASK'">
                  <b-dropdown
                    v-if="!publicTicket && ticket.type !== 'TICKET_TYPE_TASK'"
                    right
                    class="watcher-element"
                    toggle-class="text-decoration-none pl-0 border-0 transparent icon-dropdown text-left"
                    no-caret>
                    <template #button-content>
                      <coozzy-watcher-icon
                        :class="ticket.watchers && ticket.watchers.find(x => x.userId === $store.getters['user/getUserId']) ? 'active-watcher mr-1' : ''" />
                      <label
                        class="length-watchers"
                        :class="ticket.watchers && ticket.watchers.find(x => x.userId === $store.getters['user/getUserId']) ? 'active' : ''">
                        {{ ticket.watchers.length }} </label>
                    </template>
                    <template
                      v-if="!ticket.assignee || ticket.assignee.userId !== userId">
                      <b-dropdown-item
                        v-if="ticket.watchers && ticket.watchers.find(x => x.userId === $store.getters['user/getUserId'])"
                        data-cy="ticket-remove-watcher-btn"
                        @click="removeWatcher">
                        <coozzy-watcher-icon />
                        {{ $t('message.ticketOverview.ticket.watcher.stopWatcher') }}
                      </b-dropdown-item>
                      <b-dropdown-item
                        v-else-if="isEditor || isCompanyAdmin || isTicketEditor || isTicketReader || isTicketCreator"
                        data-cy="ticket-add-watcher-btn"
                        @click="addWatcher">
                        <coozzy-watcher-icon />
                        {{ $t('message.ticketOverview.ticket.watcher.startWatcher') }}
                      </b-dropdown-item>
                      <b-dropdown-divider
                        v-if="isEditor || isCompanyAdmin || isTicketEditor || isTicketReader || isTicketCreator || ticket.watchers.find(x => x.userId === $store.getters['user/getUserId'])" />
                    </template>
                    <b-dropdown-text>{{ $t('message.ticketOverview.ticket.watcher.watchingIssue') }}</b-dropdown-text>
                    <b-dropdown-divider />
                    <b-dropdown-group
                      v-for="(watc, index) in ticket.watchers"
                      :key="watc.accountId + '_' + index">
                      <b-dropdown-item>
                        <img
                          height="25"
                          class="avatar"
                          :alt="watc.name"
                          :src="`${watc.profilePictureUrl ? watc.profilePictureUrl : '/avatar.png'}`"> {{ watc.name }}
                      </b-dropdown-item>
                    </b-dropdown-group>
                  </b-dropdown>
                </div>
                <div
                  v-if="!publicTicket && ticket.type === 'TICKET_TYPE_ORDER'"
                  class="col-12 col-md-3">
                  <label>{{ $t('message.createOrder.statusUpdate.title') }}</label>
                  <span
                    v-if="countExternalWatchers > 0"
                    class="iconsStatusUpdate mb-1">
                    <br>
                    <coozzy-success-icon
                      v-b-tooltip.hover.html="{ container: '#ticketDetail', title: getWatchersNames() }" />
                  </span>
                  <span
                    v-else
                    class="iconsStatusUpdate mb-1">
                    <br><coozzy-fail-icon />
                  </span>
                </div>
                <div
                  v-if="ticket.type === 'TICKET_TYPE_ORDER' && mailOutboundList.length > 0 && orderMailOutboundEntry && Number(ticket.createdAt) >= 1709638200"
                  class="col-12 col-md-5">
                  <label class="d-block"> {{ $t('message.tableColumnHeadings.receivedOpened') }}: </label>
                  <template v-if="orderMailOutboundEntry.mailType === 'MAIL_TYPE_IMIP'">
                    -
                  </template>
                  <template v-else>
                    <template v-if="orderMailOutboundEntry.deliverData?.status === 'DELIVER_STATUS_DELIVERED'">
                      <coozzy-success-icon />
                    </template>
                    <template v-else-if="orderMailOutboundEntry.deliverData?.status === 'DELIVER_STATUS_PENDING'">
                      <coozzy-pending-icon />
                    </template>
                    <template v-else>
                      <coozzy-fail-icon
                        v-b-tooltip.hover.left
                        :title="orderMailOutboundEntry.deliverData?.errorMessage" />
                    </template>
                    /
                    <template v-if="orderMailOutboundEntry.engagementData?.status === 'ENGAGEMENT_STATUS_OPENED'">
                      <coozzy-success-icon />
                    </template>
                    <template v-else>
                      <coozzy-fail-icon />
                    </template>
                  </template>
                </div>
              </div>
              <div class="row">
                <div
                  v-if="!publicTicket && ticket.type === 'TICKET_TYPE_TASK'"
                  class="col-12"
                  :class="ticket.startDate ? 'col-md-3' : 'col-md-4'">
                  <label v-if="!publicTicket && ticket.type === 'TICKET_TYPE_TASK'">{{
                    $t('message.createOrder.watcher')
                  }}</label>
                  <b-dropdown
                    v-if="!publicTicket && ticket.type === 'TICKET_TYPE_TASK'"
                    right
                    class="watcher-element col-12 p-0"
                    toggle-class="text-decoration-none border-0 transparent icon-dropdown text-left"
                    no-caret>
                    <template #button-content>
                      <coozzy-watcher-icon
                        :class="ticket.watchers && ticket.watchers.find(x => x.userId === $store.getters['user/getUserId']) ? 'active-watcher mr-1' : ''" />
                      <label
                        class="length-watchers"
                        :class="ticket.watchers && ticket.watchers.find(x => x.userId === $store.getters['user/getUserId']) ? 'active' : ''">
                        {{ ticket.watchers.length }} </label>
                    </template>
                    <template
                      v-if="!ticket.assignee || ticket.assignee.userId !== userId">
                      <b-dropdown-item
                        v-if="ticket.watchers && ticket.watchers.find(x => x.userId === $store.getters['user/getUserId'])"
                        data-cy="ticket-remove-watcher-btn"
                        @click="removeWatcher">
                        <coozzy-watcher-icon />
                        {{ $t('message.ticketOverview.ticket.watcher.stopWatcher') }}
                      </b-dropdown-item>
                      <b-dropdown-item
                        v-else-if="isEditor || isCompanyAdmin || isTicketEditor || isTicketReader || isTicketCreator"
                        data-cy="ticket-add-watcher-btn"
                        @click="addWatcher">
                        <coozzy-watcher-icon />
                        {{ $t('message.ticketOverview.ticket.watcher.startWatcher') }}
                      </b-dropdown-item>
                      <b-dropdown-divider
                        v-if="isEditor || isCompanyAdmin || isTicketEditor || isTicketReader || isTicketCreator || ticket.watchers.find(x => x.userId === $store.getters['user/getUserId'])" />
                    </template>
                    <b-dropdown-text>{{ $t('message.ticketOverview.ticket.watcher.watchingIssue') }}</b-dropdown-text>
                    <b-dropdown-divider />
                    <b-dropdown-group
                      v-for="(watc, index) in ticket.watchers"
                      :key="watc.accountId + '_' + index">
                      <b-dropdown-item>
                        <img
                          height="25"
                          class="avatar"
                          :alt="watc.name"
                          :src="`${watc.profilePictureUrl ? watc.profilePictureUrl : '/avatar.png'}`"> {{ watc.name }}
                      </b-dropdown-item>
                    </b-dropdown-group>
                  </b-dropdown>
                </div>
                <div
                  v-if="ticket.priority"
                  class="col-12"
                  :class="ticket.type === 'TICKET_TYPE_ORDER' || (ticket.type === 'TICKET_TYPE_TASK' && ticket.startDate) ? 'col-md-3' : 'col-md-4'">
                  <label>{{ $t('message.ticketOverview.ticket.priority.title') }}</label>
                  <p
                    class="mb-1">
                    <img
                      :alt="String($t('message.ticketOverview.ticket.priority.' + ticket.priority))"
                      :src="`/img/priority/prio-${ticket.priority}.svg`"
                      height="15px">
                    {{ $t('message.ticketOverview.ticket.priority.' + ticket.priority) }}
                  </p>
                </div>
                <div
                  v-if="ticket.startDate"
                  class="col-12"
                  :class="ticket.type === 'TICKET_TYPE_ORDER' || (ticket.type === 'TICKET_TYPE_TASK' && ticket.startDate) ? 'col-md-3' : 'col-md-4'">
                  <label>{{ $t('message.ticketOverview.ticket.orderBeExecutedFrom') }}</label>
                  <p
                    class="mb-1">
                    {{ formatTicketDateDisplay(ticket.startDate) }}
                  </p>
                </div>
                <div
                  v-if="ticket.dueDate"
                  class="col-12"
                  :class="ticket.type === 'TICKET_TYPE_ORDER' || (ticket.type === 'TICKET_TYPE_TASK' && ticket.startDate) ? 'col-md-3' : 'col-md-4'">
                  <label>{{ $t('message.ticketOverview.ticket.dueDate') }}</label>
                  <p
                    class="mb-1">
                    {{ formatTicketDateDisplay(ticket.dueDate) }}
                  </p>
                </div>
              </div>
              <template
                v-if="!publicTicket && ticket.costsEntries && ticket.costsEntries.length">
                <div class="row">
                  <div
                    class="col-12 col-md-3">
                    <label>{{ $t('message.ticketOverview.ticket.costs.room') }}</label>
                  </div>
                  <div
                    class="col-12 col-md-3">
                    <label>{{ $t('message.ticketOverview.ticket.costs.costAbsorption') }}</label>
                  </div>
                  <div
                    class="col-12 col-md-3">
                    <label>{{ $t('message.ticketOverview.ticket.costs.title') }}</label>
                  </div>
                </div>
                <div
                  v-for="(entry, index) in ticket.costsEntries"
                  :key="index"
                  class="row">
                  <div
                    class="col-12 col-md-3">
                    <p class="mb-1">
                      {{ entry.room }}
                    </p>
                  </div>
                  <div
                    class="col-12 col-md-3">
                    <p class="mb-1">
                      {{ formatCostAbsorption(entry.costAbsorption) }}
                    </p>
                  </div>
                  <div
                    class="col-12 col-md-3">
                    <p class="mb-1">
                      {{ formatCostShareValue(entry) }}
                    </p>
                  </div>
                </div>
              </template>
              <div
                v-else-if="!publicTicket && ticket.costs"
                class="row">
                <div
                  v-if="ticket.costs.costAbsorption !== 'COST_ABSORPTION_UNDEFINED'"
                  class="col-12"
                  :class="ticket.type === 'TICKET_TYPE_ORDER' ? 'col-md-3' : 'col-md-4'">
                  <label>{{ $t('message.ticketOverview.ticket.costs.costAbsorption') }}</label>
                  <p
                    class="mb-1">
                    {{ $t('message.ticketOverview.ticket.costs.costAbsorptionValues.' + ticket.costs.costAbsorption) }}
                  </p>
                </div>
                <div
                  v-if="ticket.costs.costShareUnit !== 'COST_SHARE_UNIT_UNDEFINED'"
                  class="col-12"
                  :class="ticket.type === 'TICKET_TYPE_ORDER' ? 'col-md-3' : 'col-md-4'">
                  <label>{{ $t('message.ticketOverview.ticket.costs.title') }}</label>
                  <p
                    v-if="ticket.costs.costShareUnit === 'COST_SHARE_UNIT_PERCENT'"
                    class="mb-1">
                    {{ ticket.costs.costShareValue }}%
                  </p>
                  <p
                    v-else
                    class="mb-1">
                    {{ formatCostShareChfDisplay(ticket.costs.costShareValue) }}
                  </p>
                </div>
              </div>
              <div
                v-b-toggle.collapse-detail
                class="mt-3 collapse-title collapse-detail">
                <h5 class="cursor-pointer">
                  {{ $t('message.ticketOverview.ticket.details') }}
                </h5>
              </div>
              <b-collapse
                :id="'collapse-detail'"
                :visible="true">
                <div class="row mb-2">
                  <template
                    v-for="(data, index) in orderData">
                    <template
                      v-if="data === 'assignee'">
                      <div
                        v-if="!ticket.assignee"
                        :key="'info_assignee_empty_' + index"
                        class="col-12 col-md-6 mb-2">
                        <label>{{ $t('message.ticketOverview.ticket.assignee') }}</label>
                        <p
                          class="m-0">
                          <img
                            class="avatar"
                            :alt="'avatar'"
                            :src="'/avatar.png'"> {{ $t('message.generic.unassigned') }}
                        </p>
                      </div>
                      <div
                        v-else
                        :key="'info_assignee_filled_' + index"
                        class="col-12 col-md-6 mb-2">
                        <label>{{ $t('message.ticketOverview.ticket.assignee') }}</label>
                        <p
                          class="m-0">
                          <template v-if="isAileanAssignee">
                            <img
                              src="@/assets/integrations/ailean.svg"
                              alt="ailean"
                              height="20px">
                            ailean
                          </template>
                          <template v-else>
                            <img
                              class="avatar"
                              :alt="ticket.assignee.name"
                              :src="ticket.assignee.profilePictureUrl !== '' ? ticket.assignee.profilePictureUrl : '/avatar.png'">
                            {{ ticket.assignee.name }}
                          </template>
                        </p>
                        <template v-if="!isAileanAssignee">
                          <p
                            v-if="ticket.assignee.accountName"
                            class="m-0">
                            <span>{{ ticket.assignee.accountName }}</span>
                          </p>
                          <p
                            v-if="ticket.assignee.address"
                            class="m-0">
                            <address-text
                              :hide-if-empty="true"
                              :address="ticket.assignee.address" />
                          </p>
                          <p
                            v-if="ticket.assignee.email && ticket.type !== 'TICKET_TYPE_ORDER'"
                            class="m-0">
                            <span>{{ ticket.assignee.email }}</span>
                          </p>
                          <p
                            v-if="ticket.assignee.phone"
                            class="m-0">
                            <span>{{ ticket.assignee.phone }}</span>
                          </p>
                        </template>
                      </div>
                    </template>
                    <div
                      v-if="data === 'operator' && ticket.correspondences.length !== 0"
                      :key="'info_' + index"
                      class="col-12 col-md-6 mb-2">
                      <label>{{ $t('message.ticketOverview.ticket.operator') }}</label>
                      <div
                        v-for="cor in ticket.correspondences"
                        :key="'operator_' + cor.contactId">
                        <p
                          class="m-0">
                          <span>{{ cor.name }}</span>
                        </p>
                        <p
                          v-if="cor.companyName !== ''"
                          class="m-0">
                          <span>{{ cor.companyName }}</span>
                        </p>
                        <p
                          v-if="cor.address"
                          class="m-0">
                          <address-text
                            :hide-if-empty="true"
                            :address="cor.address" />
                        </p>
                        <p
                          v-if="cor.email"
                          class="m-0">
                          <span>{{ cor.email }}</span>
                        </p>
                        <p
                          v-if="cor.phone"
                          class="m-0">
                          <span>{{ cor.phone }}</span>
                        </p>
                        <p
                          v-else-if="cor.phoneNumbers && cor.phoneNumbers.length > 0"
                          class="m-0">
                          <span
                            v-if="getPhoneNumbers(cor.phoneNumbers, 'mobile') !== '-'">{{
                              getPhoneNumbers(cor.phoneNumbers, 'mobile')
                            }}</span>
                          <span
                            v-else-if="getPhoneNumbers(cor.phoneNumbers, 'work') !== '-'">{{
                            getPhoneNumbers(cor.phoneNumbers, 'work')
                          }}</span>
                          <span
                            v-else-if="getPhoneNumbers(cor.phoneNumbers, 'home') !== '-'">{{
                            getPhoneNumbers(cor.phoneNumbers, 'home')
                          }}</span>
                        </p>
                      </div>
                    </div>
                    <div
                      v-if="data === 'creator' && !publicTicket && ticket.creator"
                      :key="'info_' + index"
                      class="col-12 col-md-6 mb-2">
                      <label>{{ $t('message.ticketOverview.ticket.creator') }}</label>
                      <p
                        v-if="ticket.creator.userId === 'aic'"
                        class="m-0">
                        <img
                          src="@/assets/integrations/aic.svg"
                          alt="AIC"
                          height="10px">
                      </p>
                      <p
                        v-else-if="isAileanCreator"
                        class="m-0">
                        <img
                          src="@/assets/integrations/ailean.svg"
                          alt="ailean"
                          height="16px">
                        ailean
                      </p>
                      <template v-else>
                        <p
                          class="m-0">
                          <img
                            v-if="ticket.creator.userId !== 'system'"
                            class="avatar"
                            :alt="ticket.creator.name"
                            :src="ticket.creator.profilePictureUrl !== '' ? ticket.creator.profilePictureUrl : '/avatar.png'">
                          <template v-if="ticket.creator.userId === 'system'">
                            {{ $t('message.ticketOverview.ticket.createdBySystem') }}
                          </template>
                          <template v-else>
                            {{ ticket.creator.name }}
                          </template>
                        </p>
                        <template v-if="ticket.creator.userId !== 'system'">
                          <p
                            v-if="ticket.creator.accountName"
                            class="m-0">
                            <span>{{ ticket.creator.accountName }}</span>
                          </p>
                          <p
                            v-if="ticket.creator.address"
                            class="m-0">
                            <address-text
                              :hide-if-empty="true"
                              :address="ticket.creator.address" />
                          </p>
                          <p
                            v-if="ticket.creator.email"
                            class="m-0">
                            <span>{{ ticket.creator.email }}</span>
                          </p>
                          <p
                            v-if="ticket.creator.phone"
                            class="m-0">
                            <span>{{ ticket.creator.phone }}</span>
                          </p>
                        </template>
                      </template>
                    </div>
                    <!-- this part of code for the new ticket that has onSiteContacts (array) -->
                    <div
                      v-if="data === 'onsite' && ticket.onSiteContacts.length > 0"
                      :key="'info_onsite_contacts_' + index"
                      class="col-12 col-md-6 mb-2">
                      <label>{{ $t('message.ticketOverview.ticket.contactPersonOnSite') }}</label>
                      <div
                        v-for="(onSiteContact, j) in ticket.onSiteContacts"
                        :key="'onSiteContact_' + j"
                        :class="'mb-1'">
                        <p
                          class="m-0">
                          <span>{{ onSiteContact.name }}</span>
                        </p>
                        <template>
                          <p
                            v-if="onSiteContact.companyName !== ''"
                            class="m-0">
                            <span>{{ onSiteContact.companyName }}</span>
                          </p>
                          <p
                            v-if="onSiteContact.address"
                            class="m-0">
                            <address-text
                              :hide-if-empty="true"
                              :address="onSiteContact.address" />
                          </p>
                          <p
                            v-if="onSiteContact.email"
                            class="m-0">
                            <span>{{ onSiteContact.email }}</span>
                          </p>
                          <div v-if="onSiteContact.phoneNumbers && onSiteContact.phoneNumbers.length > 0">
                            <p
                              v-if="getPhoneNumbers(onSiteContact.phoneNumbers, 'mobile') !== '-'"
                              class="m-0">
                              <b>{{ $t('message.generic.phone.mobile') }}:</b>
                              {{ getPhoneNumbers(onSiteContact.phoneNumbers, 'mobile') }}
                            </p>
                            <p
                              v-if="getPhoneNumbers(onSiteContact.phoneNumbers, 'work') !== '-'"
                              class="m-0">
                              <b>{{ $t('message.generic.phone.work') }}:</b>
                              {{ getPhoneNumbers(onSiteContact.phoneNumbers, 'work') }}
                            </p>
                            <p
                              v-if="getPhoneNumbers(onSiteContact.phoneNumbers, 'home') !== '-'"
                              class="m-0">
                              <b>{{ $t('message.generic.phone.home1') }}:</b>
                              {{ getPhoneNumbers(onSiteContact.phoneNumbers, 'home') }}
                            </p>
                            <p
                              v-if="getPhoneNumbers(onSiteContact.phoneNumbers, 'home', 1) !== '-'"
                              class="m-0">
                              <b>{{ $t('message.generic.phone.home2') }}:</b>
                              {{ getPhoneNumbers(onSiteContact.phoneNumbers, 'home', 1) }}
                            </p>
                            <div class="mb-2" />
                          </div>
                          <div v-else-if="onSiteContact.phone !== ''">
                            <p class="m-0">
                              <b>{{ $t('message.generic.phone.mobile') }}:</b>
                              <span>{{ onSiteContact.phone }}</span>
                            </p>
                          </div>
                        </template>
                      </div>
                    </div>
                    <!-- this part of code for the old ticket that has only onSiteContact (object) -->
                    <div
                      v-else-if="data === 'onsite' && ticket.onSiteContact"
                      :key="'info_onsite_contact_' + index"
                      class="col-12 col-md-6 mb-2">
                      <label>{{ $t('message.ticketOverview.ticket.contactPersonOnSite') }}</label>
                      <p
                        class="m-0">
                        <span>{{ ticket.onSiteContact.name }}</span>
                      </p>
                      <p
                        v-if="ticket.onSiteContact.companyName !== ''"
                        class="m-0">
                        <span>{{ ticket.onSiteContact.companyName }}</span>
                      </p>
                      <p
                        v-if="ticket.onSiteContact.address"
                        class="m-0">
                        <address-text
                          :hide-if-empty="true"
                          :address="ticket.onSiteContact.address" />
                      </p>
                      <p
                        v-if="ticket.onSiteContact.email"
                        class="m-0">
                        <span>{{ ticket.onSiteContact.email }}</span>
                      </p>
                      <p
                        v-if="ticket.onSiteContact?.phone"
                        :key="'phone_' + ticket.onSiteContact.contactId"
                        class="m-0">
                        <span>{{ ticket.onSiteContact.phone }}</span>
                      </p>
                      <p
                        v-else-if="ticket.onSiteContact.phoneNumbers && ticket.onSiteContact.phoneNumbers.length > 0"
                        class="m-0">
                        <span
                          v-if="getPhoneNumbers(ticket.onSiteContact.phoneNumbers, 'mobile') !== '-'">{{
                            getPhoneNumbers(ticket.onSiteContact.phoneNumbers, 'mobile')
                          }}</span>
                        <span
                          v-else-if="getPhoneNumbers(ticket.onSiteContact.phoneNumbers, 'work') !== '-'">{{
                          getPhoneNumbers(ticket.onSiteContact.phoneNumbers, 'work')
                        }}</span>
                        <span
                          v-else-if="getPhoneNumbers(ticket.onSiteContact.phoneNumbers, 'home') !== '-'">{{
                          getPhoneNumbers(ticket.onSiteContact.phoneNumbers, 'home')
                        }}</span>
                      </p>
                    </div>
                  </template>
                </div>
              </b-collapse>
            </b-card>
            <b-card
              v-if="!publicTicket && references.length !== 0">
              <div
                v-b-toggle.collapse-connections
                class="collapse-detail collapse-title">
                <h5 class="cursor-pointer">
                  {{ $t('message.ticketOverview.ticket.connections') }}
                </h5>
              </div>
              <b-collapse
                id="collapse-connections"
                :visible="true">
                <div
                  class="row mb-2 connection">
                  <div
                    v-for="(ref, index) in references"
                    :key="index"
                    class="col-12 d-flex align-items-center">
                    <div class="containerCircles">
                      <div
                        v-if="ref.tenancy"
                        v-b-tooltip="{container: '#collapse-connections', placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.tenancy')}"
                        class="color-box mt-0 ml-2 mr-2 tenancy" />
                      <div
                        v-if="ref.condominium"
                        v-b-tooltip="{container: '#collapse-connections', placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.condo_owner')}"
                        class="color-box mt-0 ml-2 mr-2 condo_owner" />
                      <div
                        v-if="ref.object && ref.object.condominiumOwnership === true && !ref.tenancy && !ref.condominium && !ref.device && !ref.request"
                        v-b-tooltip="{container: '#collapse-connections', placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.condominium')}"
                        class="color-box mt-0 ml-2 mr-2 condominium" />
                      <div
                        v-if="ref.object && ref.object.condominiumOwnership === false && !ref.tenancy && !ref.condominium && !ref.device && !ref.request"
                        v-b-tooltip="{container: '#collapse-connections', placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.object')}"
                        class="color-box mt-0 ml-2 mr-2 object" />
                      <div
                        v-if="ref.device"
                        v-b-tooltip="{container: '#collapse-connections',placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.device')}"
                        class="color-box mt-0 ml-2 mr-2 device" />
                      <div
                        v-if="ref.mortgage"
                        v-b-tooltip="{container: '#collapse-connections',placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.mortgage')}"
                        class="color-box mt-0 ml-2 mr-2 mortgage" />
                      <div
                        v-if="ref.insurance"
                        v-b-tooltip="{container: '#collapse-connections',placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.insurance')}"
                        class="color-box mt-0 ml-2 mr-2 insurance" />
                      <div
                        v-if="ref.building && !ref.object && !ref.device && !ref.mortgage && !ref.insurance"
                        v-b-tooltip="{container: '#collapse-connections',placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.building')}"
                        class="color-box mt-0 ml-2 mr-2 not_activated" />
                      <div
                        v-if="ref.property && !ref.building && !ref.device && !ref.mortgage && !ref.insurance"
                        v-b-tooltip="{container: '#collapse-connections',placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.property')}"
                        class="color-box mt-0 ml-2 mr-2 current" />
                      <div
                        v-if="ref.contact"
                        v-b-tooltip="{container: '#collapse-connections',placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.contact')}"
                        class="color-box mt-0 ml-2 mr-2 terminated" />
                      <div
                        v-if="ref.request"
                        v-b-tooltip="{container: '#collapse-connections',placement : 'left', title : $t('message.ticketOverview.ticket.connection_items.request')}"
                        class="color-box mt-0 ml-2 mr-2 request" />
                    </div>
                    <div class="">
                      <span v-if="ref.property">
                        <router-link
                          v-if="!isMarketingModule"
                          :to="redirectToPropertyDetail(ref.property.objectID || ref.property.id)">
                          {{ ref.property.name }}
                        </router-link>
                        <span v-else>{{ ref.property.name }}</span>
                      </span>
                      <span v-if="ref.building">
                        <span v-if="ref.property"> > </span>
                        <router-link
                          v-if="!isMarketingModule"
                          :to="redirectToBuildingDetail(ref.building.objectID || ref.building.id)">
                          {{ ref.building.name }}
                        </router-link>
                        <span v-else>{{ ref.building.name }}</span>
                      </span>
                      <span v-if="ref.object">
                        <span v-if="ref.building"> > </span>
                        <router-link
                          v-if="!isMarketingModule"
                          :to="redirectToDetail(ref.object.buildingId, ref.object.objectID || ref.object.id, 'objects')">
                          {{
                            getObjectConnectionLabel(ref.object)
                          }}
                        </router-link>
                        <span v-else>{{
                          getObjectConnectionLabel(ref.object)
                        }}</span>
                      </span>
                      <span v-if="ref.room">
                        <span v-if="ref.object || ref.building || ref.property"> > </span>
                        <a
                          v-if="!isMarketingModule && canOpenRoomConnection(ref)"
                          class="external-link cursor-pointer"
                          href="#"
                          @click.prevent="openRoomConnection(ref)"
                          @mousedown.middle.prevent="openRoomConnection(ref, true)">
                          {{ ref.room.name }}
                        </a>
                        <span v-else>{{ ref.room.name }}</span>
                      </span>
                      <span v-if="ref.mortgage">
                        <template v-if="ref.property">
                          <span> > </span>
                          <span
                            v-if="!isMarketingModule"
                            @click="setMortgageId('property', ref.mortgage.id)"
                            @click.middle="setMortgageId('property', ref.mortgage.id)">
                            <router-link
                              v-if="!isMarketingModule"
                              :to="redirectToPropertyDetail(ref.property.id)">
                              {{
                                ref.mortgage.bankName + ' - ' + $t('message.onBoarding.mortgage.mortgageDesignation.' + ref.mortgage.mortgageDesignation)
                              }}
                            </router-link>
                          </span>
                          <span v-else>{{
                            ref.mortgage.bankName + ' - ' + $t('message.onBoarding.mortgage.mortgageDesignation.' + ref.mortgage.mortgageDesignation)
                          }}</span>
                        </template>
                        <template v-else-if="ref.building">
                          <span> > </span>
                          <span v-if="!isMarketingModule">
                            <router-link
                              :to="redirectToBuildingDetail(ref.building.id)">
                              {{
                                ref.mortgage.bankName + ' - ' + $t('message.onBoarding.mortgage.mortgageDesignation.' + ref.mortgage.mortgageDesignation)
                              }}
                            </router-link>
                          </span>
                          <span v-else>{{
                            ref.mortgage.bankName + ' - ' + $t('message.onBoarding.mortgage.mortgageDesignation.' + ref.mortgage.mortgageDesignation)
                          }}</span>
                        </template>
                      </span>
                      <span v-if="ref.insurance">
                        <template v-if="ref.building">
                          <span> > </span>
                          <span
                            v-if="!isMarketingModule"
                            @click="setInsuranceId('building', ref.insurance.id)"
                            @click.middle="setInsuranceId('building', ref.insurance.id)">
                            <router-link
                              v-if="!isMarketingModule"
                              :to="redirectToBuildingDetail(ref.building.id)">
                              {{ $t('message.onBoarding.buildings.insurances.type.' + ref.insurance.type) }}
                            </router-link>
                          </span>
                          <span v-else>{{
                            $t('message.onBoarding.buildings.insurances.type.' + ref.insurance.type)
                          }}</span>
                        </template>
                        <template v-if="ref.property && !ref.building">
                          <span> > </span>
                          <span
                            v-if="!isMarketingModule"
                            @click="setInsuranceId('property', ref.insurance.id)"
                            @click.middle="setInsuranceId('property', ref.insurance.id)">
                            <router-link
                              v-if="!isMarketingModule"
                              :to="redirectToPropertyDetail(ref.property.id)">
                              {{ $t('message.onBoarding.buildings.insurances.type.' + ref.insurance.type) }}
                            </router-link>
                          </span>
                          <span v-else>{{
                            $t('message.onBoarding.buildings.insurances.type.' + ref.insurance.type)
                          }}</span>
                        </template>
                      </span>
                      <span
                        v-if="ref.device"
                        :class="[ref.device.uninstallation !== null ? 'text-gray' : '']">
                        <span v-if="ref.property || ref.building || ref.object"> > </span>
                        <router-link
                          v-if="!isMarketingModule && redirectToDeviceDetail(ref.device.referenceId, ref.device.objectID || ref.device.id)"
                          :to="redirectToDeviceDetail(ref.device.referenceId, ref.device.objectID || ref.device.id)">
                          <template v-if="ref.device.type && ref.device.type !== 'DEVICE_TYPE_UNDEFINED'">
                            {{ getDeviceTypeLabel(ref.device.type) }}
                          </template>
                          <template v-else>
                            -
                          </template>
                        </router-link>
                        <span v-else>
                          <template v-if="ref.device.type && ref.device.type !== 'DEVICE_TYPE_UNDEFINED'">
                            {{ getDeviceTypeLabel(ref.device.type) }}
                          </template>
                          <template v-else>
                            -
                          </template>
                        </span>
                      </span>
                      <span v-if="ref.object && ref.tenancy">
                        <span v-if="ref.object"> > </span>
                        <router-link
                          v-if="!isMarketingModule"
                          :to="redirectToDetail(ref.object.buildingId, ref.tenancy.objectID || ref.tenancy.id, 'tenants')">
                          <template v-if="ref.tenancy.tenants">
                            <span
                              v-for="(contact, j) in ref.tenancy.tenants"
                              :key="j">
                              {{ contact.name }}
                            </span>
                          </template>
                          <template
                            v-else-if="ref.tenancy.tenant && ref.listContact && ref.tenancy.tenant.contactIds.length > 0">
                            <span
                              v-for="(contact, j) in ref.tenancy.tenant.contactIds"
                              :key="j">
                              <template v-if="ref.listContact.find(x => x.id === contact)">
                                {{ ref.listContact.find(x => x.id === contact).name }} <span
                                  v-if="j < ref.tenancy.tenant.contactIds.length - 1">-</span>
                              </template>
                            </span>
                          </template>
                          <template v-else>
                            <span
                              v-for="(contact, j) in ref.listContact"
                              :key="j">
                              {{ contact.name }}
                            </span>
                          </template>
                        </router-link>
                        <span v-else>
                          <template v-if="ref.tenancy.tenants">
                            <span
                              v-for="(contact, j) in ref.tenancy.tenants"
                              :key="j">
                              {{ contact.name }}
                            </span>
                          </template>
                          <template
                            v-else-if="ref.tenancy.tenant && ref.listContact && ref.tenancy.tenant.contactIds.length > 0">
                            <span
                              v-for="(contact, j) in ref.tenancy.tenant.contactIds"
                              :key="j">
                              <template v-if="ref.listContact.find(x => x.id === contact)">
                                {{ ref.listContact.find(x => x.id === contact).name }} <span
                                  v-if="j < ref.tenancy.tenant.contactIds.length - 1">-</span>
                              </template>
                            </span>
                          </template>
                          <template v-else>
                            <span
                              v-for="(contact, j) in ref.listContact"
                              :key="j">
                              {{ contact.name }}
                            </span>
                          </template>
                        </span>
                      </span>
                      <span v-if="ref.object && ref.condominium">
                        <span v-if="ref.object"> > </span>
                        <router-link
                          v-if="!isMarketingModule"
                          :to="redirectToDetail(ref.object.buildingId, ref.condominium.objectID || ref.condominium.id, 'condominium')">
                          <template v-if="ref.condominium.condominiumOwners">
                            <span
                              v-for="(contact, j) in ref.condominium.condominiumOwners"
                              :key="j">
                              {{ contact.name }}
                            </span>
                          </template>
                          <template v-else>
                            <span
                              v-for="(contact, j) in ref.listContact"
                              :key="j">
                              {{ contact.name }}
                            </span>
                          </template>
                        </router-link>
                        <span v-else>
                          <template v-if="ref.condominium.condominiumOwners">
                            <span
                              v-for="(contact, j) in ref.condominium.condominiumOwners"
                              :key="j">
                              {{ contact.name }}
                            </span>
                          </template>
                          <template v-else>
                            <span
                              v-for="(contact, j) in ref.listContact"
                              :key="j">
                              {{ contact.name }}
                            </span>
                          </template>
                        </span>
                      </span>
                      <span v-if="ref.contact">
                        <router-link
                          v-if="!isMarketingModule"
                          :to="redirectToContactDetail(getContactRouteId(ref.contact))">
                          {{ ref.contact.name }}
                        </router-link>
                        <span v-else>{{ ref.contact.name }}</span>
                      </span>
                      <span v-if="ref.request">
                        <span> > </span>
                        <template
                          v-if="!hasAccessMarketBase">
                          <span
                            v-for="(contact, j) in ref.listContact"
                            :key="j">
                            <span v-if="j > 0">- </span>
                            <router-link
                              :to="redirectToContactDetail(contact.id)">
                              {{ contact.name }}
                            </router-link>
                          </span>
                        </template>
                        <router-link
                          v-else
                          :to="redirectToRequestDetail(ref.request.objectId, ref.request.id)">
                          <span
                            v-for="(contact, j) in ref.listContact"
                            :key="j">
                            <span v-if="j > 0">- </span>{{ contact.name }}
                          </span>
                        </router-link>
                      </span>
                    </div>
                  </div>
                </div>
              </b-collapse>
            </b-card>
            <b-card
              v-if="!publicTicket && mailList.length > 0">
              <div
                v-b-toggle.collapse-linked-mail
                class="collapse-detail collapse-title mb-3">
                <h5 class="cursor-pointer">
                  {{ $t('message.ticketOverview.ticket.linkedEmail') }}
                </h5>
              </div>
              <b-collapse
                id="collapse-linked-mail"
                :visible="true">
                <div
                  class="row">
                  <div
                    v-for="item in mailList"
                    :key="item.id"
                    class="col-12 block pl-md-0">
                    <div class="pb-0 d-flex">
                      -
                      <p
                        class="ml-3 mb-2 mb-0"
                        @click="showDetail(item)">
                        <i>{{ item.timestamp | timestampToDateTime }}</i> - {{ item.subject }}
                        <coozzy-button
                          design="transparent"
                          data-cy="ticket-unlink-mail-btn"
                          class="only-on-hover pb-0 pt-0 pl-0"
                          @click="$event.stopPropagation(); $bvModal.show('confirmationModalUnlinkTicket' + item ? item.id : '')">
                          <coozzy-delete-icon />
                        </coozzy-button>
                      </p>
                    </div>
                    <b-modal
                      v-if="item"
                      :id="'confirmationModalUnlinkTicket' + item ? item.id : ''"
                      :ref="'confirmationModalUnlinkTicket'+ item ? item.id : ''"
                      data-cy="ticket-unlink-mail-modal"
                      no-close-on-backdrop
                      hide-footer
                      :title="$t('message.ticketOverview.ticket.unlinkMailConfirmation.title')">
                      <template>
                        <div class="col p-0">
                          <p>{{ $t('message.ticketOverview.ticket.unlinkMailConfirmation.text') }}</p>
                        </div>
                        <div class="col p-0">
                          <coozzy-button
                            size="small"
                            data-cy="ticket-unlink-mail-cancel-btn"
                            class="mb-0 border"
                            design="transparent"
                            @click="$bvModal.hide('confirmationModalUnlinkTicket' + item ? item.id : '')">
                            {{ $t('message.generic.cancel') }}
                          </coozzy-button>
                          <coozzy-button
                            size="small"
                            data-cy="ticket-unlink-mail-confirm-btn"
                            class="float-right mb-0"
                            design="green"
                            @click="unlinkTicket(item)">
                            {{ $t('message.generic.delete') }}
                          </coozzy-button>
                        </div>
                      </template>
                    </b-modal>
                  </div>
                </div>
              </b-collapse>
            </b-card>
            <ticket-dates
              v-if="!orderTicket"
              class="d-none d-md-block"
              :ticket="ticket" />
          </div>
        </div>
        <div
          v-if="!orderTicket && publicTicket"
          class="row d-block d-md-none">
          <div class="col-12">
            <ticket-dates
              v-if="!orderTicket"
              :ticket="ticket" />
          </div>
        </div>
      </coozzy-card>
      <b-modal
        id="confirmationModal"
        ref="confirmationModal"
        data-cy="ticket-discard-changes-modal"
        no-close-on-backdrop
        :title="$t('message.onBoardingTabs.confirmModal.confirmationTitle')"
        hide-footer>
        <p>{{ $t('message.onBoardingTabs.confirmModal.confirmationText') }}</p>
        <div class="col">
          <coozzy-button
            size="small"
            data-cy="ticket-discard-changes-cancel-btn"
            class="border mb-0"
            design="transparent"
            @click="$bvModal.hide('confirmationModal')">
            {{ $t('message.generic.cancel') }}
          </coozzy-button>
          <coozzy-button
            size="small"
            data-cy="ticket-discard-changes-confirm-btn"
            class="float-right mb-0"
            design="green"
            @click="discardChanges">
            {{ $t('message.onBoardingTabs.confirmModal.confirmationButton') }}
          </coozzy-button>
        </div>
      </b-modal>
      <b-modal
        :id="'confirmationModalDeleteTicket'"
        ref="confirmationModalDeleteTicket"
        data-cy="ticket-delete-modal"
        no-close-on-backdrop
        hide-footer
        :title="$t('message.ticketOverview.ticket.deleteConfirmation.title')">
        <template>
          <div class="col p-0">
            <p>{{ $t('message.ticketOverview.ticket.deleteConfirmation.text') }}</p>
          </div>
          <div class="col p-0">
            <coozzy-button
              size="small"
              data-cy="ticket-delete-cancel-btn"
              class="mb-0 border"
              design="transparent"
              @click="$bvModal.hide('confirmationModalDeleteTicket')">
              {{ $t('message.generic.cancel') }}
            </coozzy-button>
            <coozzy-button
              size="small"
              data-cy="ticket-delete-confirm-btn"
              class="float-right mb-0"
              design="green"
              @click="deleteTicket()">
              {{ $t('message.generic.delete') }}
            </coozzy-button>
          </div>
        </template>
      </b-modal>
      <b-modal
        :id="'confirmationModalRemoveLinkedTicket'"
        ref="confirmationModalRemoveLinkedTicket"
        data-cy="ticket-remove-linked-modal"
        no-close-on-backdrop
        hide-footer
        :title="$t('message.marketingMessages.confirmModal.confirmationTitle')">
        <div
          class="col p-0">
          <p>{{ $t('message.ticketOverview.ticket.deleteConfirmation.confirmationTextDeleteLinkedTicket') }}</p>
        </div>
        <div class="col p-0">
          <coozzy-button
            size="small"
            data-cy="ticket-remove-linked-cancel-btn"
            class="mb-0 border"
            design="transparent"
            @click="$bvModal.hide('confirmationModalRemoveLinkedTicket')">
            {{ $t('message.generic.cancel') }}
          </coozzy-button>
          <coozzy-button
            size="small"
            data-cy="ticket-remove-linked-confirm-btn"
            class="float-right mb-0"
            design="green"
            @click="removeTicketLinked()">
            {{ $t('message.generic.delete') }}
          </coozzy-button>
        </div>
      </b-modal>
      <b-modal
        id="confirmationModalTab"
        ref="confirmationModalTab"
        data-cy="ticket-tab-change-modal"
        no-close-on-backdrop
        :title="$t('message.onBoardingTabs.confirmModal.confirmationTitle')"
        hide-footer>
        <p>{{ $t('message.onBoardingTabs.confirmModal.confirmationText') }}</p>
        <div class="col">
          <coozzy-button
            size="small"
            data-cy="ticket-tab-change-cancel-btn"
            class="border mb-0"
            design="transparent"
            @click="cancelCancellation">
            {{ $t('message.generic.cancel') }}
          </coozzy-button>
          <coozzy-button
            size="small"
            data-cy="ticket-tab-change-confirm-btn"
            class="float-right mb-0"
            design="green"
            @click="confirmCancellation">
            {{ $t('message.onBoardingTabs.confirmModal.confirmationButton') }}
          </coozzy-button>
        </div>
      </b-modal>
      <b-modal
        v-if="selectedObjectVideo !== ''"
        ref="show-video"
        data-cy="ticket-video-modal"
        hide-footer
        hide-header>
        <div class="row mb-2">
          <div class="col">
            <b-embed
              type="video"
              controls>
              <source
                :src="selectedObjectVideo"
                type="video/webm">
              <source
                :src="selectedObjectVideo"
                type="video/mp4">
            </b-embed>
          </div>
        </div>
      </b-modal>
      <b-modal
        id="unregistered-name-modal"
        ref="unregistered-name-modal"
        data-cy="ticket-unregistered-name-modal"
        no-close-on-esc
        no-close-on-backdrop
        hide-header-close
        :title="$t('message.settings.mailbox.name')"
        hide-footer>
        <coozzy-form-input
          v-model="unregisteredName"
          data-cy="ticket-unregistered-name-input"
          :state="$v.unregisteredName && $v.unregisteredName.$dirty && $v.unregisteredName.$error ? false : null"
          :placeholder="$t('message.settings.mailbox.name')" />
        <div class="col mt-4">
          <coozzy-button
            size="small"
            data-cy="ticket-unregistered-name-save-btn"
            class="float-right mb-0"
            design="green"
            @click="confirmUnregisteredName">
            {{ $t('message.generic.form.save') }}
          </coozzy-button>
        </div>
      </b-modal>
      <b-modal
        v-if="showDeclineTicketModal"
        id="declineTicketModal"
        ref="declineTicketModal"
        data-cy="ticket-decline-modal"
        no-close-on-esc
        no-close-on-backdrop
        hide-header-close
        :title="$t('message.ticketOverview.status.declineOrder')"
        hide-footer>
        <coozzy-form-textarea
          id="description"
          v-model="descriptionDeclineTicket"
          data-cy="ticket-decline-reason-textarea"
          :initial="descriptionDeclineTicket"
          :value-text-area="descriptionDeclineTicket"
          :text="descriptionDeclineTicket"
          :placeholder="$t('message.ticketOverview.status.reason')"
          :show-label="false"
          :rows="5" />
        <div class="col p-0 mt-2">
          <coozzy-button
            size="small"
            data-cy="ticket-decline-cancel-btn"
            class="border mb-0"
            design="transparent"
            @click="hideModalDeclineTicket">
            {{ $t('message.generic.cancel') }}
          </coozzy-button>
          <coozzy-button
            size="small"
            data-cy="ticket-decline-confirm-btn"
            class="float-right mb-0"
            design="green"
            @click="confirmDeclineTicket">
            {{ $t('message.generic.form.save') }}
          </coozzy-button>
        </div>
      </b-modal>
      <b-modal
        id="changeConditionModal"
        ref="changeConditionModal"
        data-cy="ticket-change-condition-modal"
        no-close-on-backdrop
        hide-footer
        :title="$t('message.ticketOverview.changeConditionModal.title')">
        <template>
          <div class="col p-0">
            <div
              v-for="(device, index) in referencesDevices"
              :key="device?.id || device?.internalId">
              <template v-if="device">
                <div
                  v-if="index > 0"
                  class="my-3">
                  <hr>
                </div>
                <h5 class="mb-3">
                  {{ device.type ? $t('message.onBoarding.devices.typeValues.' + device.type) : '-' }}
                </h5>
                <div class="row mb-3">
                  <div class="col-12">
                    <label for="condition">{{ $t('message.onBoarding.devices.condition') }}</label>
                    <coozzy-form-select
                      :id="`condition-${device.id || device.internalId}`"
                      v-model="device.condition"
                      data-cy="ticket-change-condition-select"
                      class="select-entries w-100"
                      name="condition">
                      <option
                        v-for="(item, indexOption) in conditionOptions"
                        :key="indexOption"
                        :value="item">
                        {{ $t('message.onBoarding.devices.conditionOptions.' + item) }}
                      </option>
                    </coozzy-form-select>
                  </div>
                </div>
                <div class="row mb-3">
                  <div class="col-12">
                    <coozzy-form-textarea
                      v-model="device.note"
                      data-cy="ticket-change-condition-note-textarea"
                      :initial="device.note">
                      {{ $t('message.onBoarding.devices.note') }}
                    </coozzy-form-textarea>
                  </div>
                </div>
              </template>
            </div>
          </div>
          <div class="col p-0">
            <coozzy-button
              size="small"
              data-cy="ticket-change-condition-cancel-btn"
              class="mb-0 border"
              design="transparent"
              @click="$bvModal.hide('changeConditionModal')">
              {{ $t('message.generic.cancel') }}
            </coozzy-button>
            <coozzy-button
              size="small"
              data-cy="ticket-change-condition-confirm-btn"
              class="float-right mb-0"
              design="green"
              @click="updateDevicesCondition()">
              {{ $t('message.generic.form.save') }}
            </coozzy-button>
          </div>
        </template>
      </b-modal>
      <b-modal
        v-if="mainDevice"
        id="ChangeDeviceModal"
        ref="ChangeDeviceModal"
        data-cy="ticket-change-device-modal"
        no-close-on-backdrop
        hide-footer
        :title="$t('message.ticketOverview.ticket.actions.uninstallDeviceTitle')">
        <div class="col p-0">
          <p class="mb-4">
            {{ $t('message.ticketOverview.ticket.actions.uninstallDeviceDescription') }}
          </p>
          <h5 class="mb-3">
            {{ mainDevice.type ? $t('message.onBoarding.devices.typeValues.' + mainDevice.type) : '-' }}
          </h5>
          <div class="row mb-4">
            <div class="col-12">
              <coozzy-form-input
                v-model="mainDeviceUninstallDateInput"
                data-cy="ticket-change-device-date-input"
                :filter="'formatDate'"
                :state="$v.mainDeviceUninstallDateInput && $v.mainDeviceUninstallDateInput.$dirty && $v.mainDeviceUninstallDateInput.$error ? false : null"
                type="date" />
            </div>
          </div>
        </div>
        <div class="col p-0">
          <coozzy-button
            size="small"
            data-cy="ticket-change-device-cancel-btn"
            class="mb-0 border"
            design="transparent"
            @click="cancelChangeDevice">
            {{ $t('message.generic.cancel') }}
          </coozzy-button>
          <coozzy-button
            size="small"
            data-cy="ticket-change-device-confirm-btn"
            class="float-right mb-0"
            design="green"
            :disabled="!mainDeviceUninstallDateInput"
            @click="saveUninstallDate">
            {{ $t('message.generic.form.save') }}
          </coozzy-button>
        </div>
      </b-modal>
      <b-modal
        v-if="ticket"
        id="newDeviceModal"
        ref="newDeviceModal"
        data-cy="ticket-new-device-modal"
        no-close-on-backdrop
        hide-footer
        :title="$t('message.ticketOverview.ticket.actions.replaceComponent')">
        <template>
          <div class="col p-0">
            <p class="mb-4">
              {{ $t('message.ticketOverview.ticket.actions.enterNewDevice') }}
            </p>
            <device
              ref="new-device"
              :device="newDevice"
              :expand-by-default="true"
              :from-handover="true"
              :hide-room-selection="true"
              @device-update="newDeviceUpdated" />
          </div>
          <div class="col mt-2">
            <coozzy-button
              size="small"
              data-cy="ticket-new-device-cancel-btn"
              class="float-left mb-0 border"
              design="transparent"
              @click="cancelReplaceDevice">
              {{ $t('message.generic.cancel') }}
            </coozzy-button>
            <coozzy-button
              size="small"
              data-cy="ticket-new-device-confirm-btn"
              class="float-right mb-0"
              design="green"
              @click="saveNewDevice">
              {{ $t('message.generic.form.save') }}
            </coozzy-button>
          </div>
        </template>
      </b-modal>
      <b-modal
        v-if="editChecklistAssigneeModalBool"
        id="editChecklistAssigneeModal"
        ref="editChecklistAssigneeModal"
        data-cy="ticket-checklist-assignee-modal"
        no-close-on-backdrop
        hide-footer
        :title="$t('message.ticketOverview.ticket.setTaskAssignee')">
        <div class="col p-0">
          <coozzy-form-select
            v-model="newChecklistAssignee"
            data-cy="ticket-checklist-assignee-select"
            class="mb-2">
            <option :value="null">
              -
            </option>
            <option
              v-for="element in listFilteredEmployee"
              :key="element.id"
              :value="element.id">
              <template>
                {{ element.profile.firstName }} {{ element.profile.lastName }}
              </template>
            </option>
          </coozzy-form-select>
        </div>
        <div class="col p-0">
          <coozzy-button
            size="small"
            data-cy="ticket-checklist-assignee-cancel-btn"
            class="mb-0 border"
            design="transparent"
            @click="hideEditChecklistAssigneeModal">
            {{ $t('message.generic.cancel') }}
          </coozzy-button>
          <coozzy-button
            size="small"
            data-cy="ticket-checklist-assignee-save-btn"
            class="float-right mb-0"
            design="green"
            @click="saveNewAssigneeChecklist">
            {{ $t('message.generic.form.save') }}
          </coozzy-button>
        </div>
      </b-modal>
      <add-ticket-connections-modal
        v-if="addTicketConnectionsModal"
        ref="addTicketConnectionsModal"
        data-cy="ticket-add-connections-modal"
        :ticket-id="ticket.id"
        :connections="filteredReferences"
        @close-modal="closeAddTicketConnectionsModal" />
      <merge-orders-modal
        v-if="showMergeOrdersModalBool"
        ref="mergeOrdersModal"
        data-cy="ticket-merge-orders-modal"
        :main-ticket="ticket"
        :linked-orders="linkedOrders"
        @merge-orders="mergeOrders"
        @hide="hideMergeOrdersModal" />
      <upload-invoice-modal
        v-if="showUploadInvoiceModalBool"
        ref="uploadInvoiceModal"
        data-cy="ticket-upload-invoice-modal"
        :ticket-id="ticket.id"
        :public-ticket="publicTicket"
        :operator-name="unregisteredName"
        @uploaded-invoice="uploadedInvoice"
        @hide="hideUploadInvoiceModal"
        @proceed-without-invoice="proceedWithoutInvoice" />
      <report-damage-insurance-modal
        :visible.sync="showReportDamageInsurance"
        data-cy="ticket-report-damage-modal"
        :ticket-entries="[ticket]"
        @hide="showReportDamageInsurance = false" />
    </section>
  </div>
</template>

<script lang="ts">
import TicketApi from '@/misc/apis/TicketApi'
import MediaApi from '@/misc/apis/MediaApi'
import NoteApi from '@/misc/apis/NoteApi'
import MessageApi from '@/misc/apis/MessageApi'
import ObjectApi from '@/misc/apis/ObjectApi'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import ContactApi from '../../../misc/apis/ContactApi'
import { algolia } from '@/mixins/algolia'
import { routeChecks } from '@/mixins/routeChecks'
import { VueAutosuggest } from 'vue-autosuggest'
import { responsivity } from '@/mixins/responsivity'
import RequestApi from '@/marketingBasic/api/RequestApi'
import CoozzyWatcherIcon from '@/framework/components/icons/CoozzyWatcherIcon.vue'
import Vue, { PropType } from 'vue'
import { required } from 'vuelidate/lib/validators'
import CoozzyButton from '@/framework/components/button/CoozzyButton.vue'
import CoozzyCard from '@/framework/components/card/CoozzyCard.vue'
import CoozzyDeleteIcon from '@/framework/components/icons/CoozzyDeleteIcon.vue'
import CoozzyFormSelect from '@/framework/components/form/select/CoozzyFormSelect.vue'
import { user } from '@/mixins/user'
import Comment from '@/management/components/ticket/Comment.vue'
import TicketHistory from '@/management/components/ticket/TicketHistory.vue'
import { dateUtils } from '@/mixins/dateUtils'
import CoozzyPageTitle from '@/framework/layout/CoozzyPageTitle.vue'
import AddressText from '@/framework/components/misc/AddressText.vue'
import { saveAs } from 'file-saver'
import RoomApi from '../../../misc/apis/RoomApi'
import { formatting } from '@/mixins/formatting'
import { contact } from '@/mixins/contact'
import { mail } from '@/mixins/mail'
import { ticket } from '@/mixins/ticket'
import { ticketConnections } from '@/mixins/ticketConnections'
import { deviceUtils } from '@/mixins/deviceUtils'
import { buildDeviceDetailQuery } from '@/misc/utils/deviceNavigation'
import { isAileanTicket, isAileanUser } from '@/misc/utils/ticketUtils'
import CoozzyFileDownloadIcon from '@/framework/components/icons/CoozzyFileDownloadIcon.vue'
import CoozzyThumbnail from '@/framework/components/img/CoozzyThumbnail.vue'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner.vue'
import CoozzyCloseIcon from '@/framework/components/icons/CoozzyCloseIcon.vue'
import CoozzyFormInput from '@/framework/components/form/input/CoozzyFormInput.vue'
import TicketDates from '@/management/components/ticket/TicketDates.vue'
import CoozzySuccessIcon from '@/framework/components/icons/CoozzySuccessIcon.vue'
import CoozzyFailIcon from '@/framework/components/icons/CoozzyFailIcon.vue'
import CoozzyFormTextarea from '@/framework/components/form/textarea/CoozzyFormTextarea.vue'
import CoozzyDropdown from '@/framework/components/dropdown/CoozzyDropdown.vue'
import CoozzyDropdownItem from '@/framework/components/dropdown/CoozzyDropdownItem.vue'
import CoozzyPendingIcon from '@/framework/components/icons/CoozzyPendingIcon.vue'
import AddTicketConnectionsModal from '@/management/components/ticket/addTicketConnectionsModal.vue'
import Device from '@/building/tabs/components/Device.vue'
import { helper } from '@/types/helper'
import CoozzyCommentIconImg from '@/framework/components/icons/CoozzyCommentIconImg.vue'
import CoozzyEnterIcon from '@/framework/components/icons/CoozzyEnterIcon.vue'
import MergeOrdersModal from '@/management/components/ticket/MergeOrdersModal.vue'
import UploadInvoiceModal from '@/management/components/ticket/UploadInvoiceModal.vue'
import Document from '@/properties/components/Document.vue'
import DeepOApi from '@/misc/apis/DeepOApi'
import AicApi from '@/misc/apis/AicApi'
import ReportDamageInsuranceModal from '@/components/modals/ReportDamageInsuranceModal.vue'
import { aic } from '@/mixins/aic'
import {
  LandingPageEnum,
  MediadocumentDocument,
  ObjectdeviceDevice,
  TicketTicket,
  V2User
} from '@/types/api'
import {
  Contact,
  FileTransmission,
  TicketAttachmentMediaRow,
  TicketCommentListItem,
  TicketCorrespondenceExtended,
  TicketLinkView,
  TicketLinkedMailRow,
  TicketMailEntry,
  TicketMediaView,
  TicketReferenceView,
  TicketTaskView,
  TicketUiState
} from '@/types/models'

function moreThanTwoChar(value: string): boolean {
  return value.replace(/ /g, '').length > 1
}

function moreThanInstallationDate(value: string, vm: Vue & { mainDevice?: ObjectdeviceDevice | null }) {
  if (!value) {
    return true
  }
  const installation = vm.mainDevice?.installation
  if (!installation) {
    return true
  }
  const installationDate = vm.$options.filters?.objectToDateInput(installation)
  if (!installationDate) {
    return true
  }
  return new Date(value).getTime() >= new Date(installationDate).getTime()
}

type DetailsChecklistTask = TicketTaskView & { assignee?: V2User | null }

export default Vue.extend({
  name: 'TicketDetailsView',
  components: {
    Document,
    MergeOrdersModal,
    UploadInvoiceModal,
    CoozzyEnterIcon,
    CoozzyCommentIconImg,
    AddTicketConnectionsModal,
    CoozzyPendingIcon,
    CoozzyDropdownItem,
    CoozzyDropdown,
    CoozzyFormTextarea,
    CoozzyFailIcon,
    CoozzySuccessIcon,
    TicketDates,
    CoozzyFormInput,
    CoozzyCloseIcon,
    CoozzySpinner,
    CoozzyThumbnail,
    CoozzyFileDownloadIcon,
    CoozzyPageTitle,
    Comment,
    CoozzyFormSelect,
    CoozzyDeleteIcon,
    CoozzyCard,
    CoozzyButton,
    VueAutosuggest,
    CoozzyWatcherIcon,
    AddressText,
    TicketHistory,
    Device,
    ReportDamageInsuranceModal
  },
  mixins: [user, aic, algolia, routeChecks, responsivity, dateUtils, formatting, contact, mail, ticket, ticketConnections, deviceUtils],
  beforeRouteUpdate(to, from, next) {
    if (to.name === from.name && from.params.id !== to.params.id) {
      next()
      if (to.params.id && to.params.id !== '') {
        this.getTicketByKey(to.params.id)
      }
    }
  },
  beforeRouteLeave(to, from, next) {
    this.nextPath = next
    if (this.hasUnsavedTicketEdits) {
      this.$refs.confirmationModal.show()
    } else {
      if (this.nextPath !== null) {
        this.nextPath()
      }
    }
  },
  props: {
    id: {
      type: String as PropType<string>,
      default: ''
    },
    email: {
      type: String as PropType<string>,
      default: ''
    },
    orderTicket: {
      type: Object as PropType<TicketUiState | null>,
      default: null
    },
    publicTicket: {
      type: Object as PropType<TicketUiState | null>,
      default: null
    }
  },
  metaInfo() {
    if (this.id !== '') {
      return {
        title: this.ticket ? (this.ticket.key ? this.ticket.key + ' - ' : '') + this.ticket.title : ''
      }
    } else if (this.publicTicket) {
      return {
        title: this.publicTicket.key + ' - ' + this.publicTicket.title
      }
    } else if (!this.orderTicket) {
      return {
        title: this.$t('message.ticketOverview.createTicket')
      }
    }
  },
  data() {
    return {
      loadingAnimation: false,
      fileTransmissions: [] as FileTransmission[],
      invoiceDocument: null as MediadocumentDocument | null,
      mailList: [] as TicketLinkedMailRow[],
      mailOutboundList: [] as TicketMailEntry[],
      selectedMail: null as TicketLinkedMailRow | null,
      dynamicId: '',
      unregisteredName: '',
      showAllComments: false,
      hasUnsavedTicketEdits: false,
      nextPath: null as (() => void) | null,
      tabIndex: 0,
      oldValTab: null as number | null,
      removeLinkedTicket: null as TicketLinkView | null,
      showLinkInputs: false,
      showCommentInputs: false,
      galleryImages: [] as TicketMediaView[],
      selectedObjectVideo: '',
      comment: '',
      refreshAutosuggestKey: '',
      contactList: [] as Contact[],
      ticketsForLink: [] as TicketTicket[],
      typeOfLink: 'TICKET_LINK_TYPE_RELATES',
      ticketToLink: '',
      searchForTickets: '',
      listTickets: [] as TicketTicket[],
      ticket: null as TicketUiState | null,
      mediasList: [] as TicketAttachmentMediaRow[],
      references: [] as TicketReferenceView[],
      availableTypes: ['TICKET_TYPE_ORDER', 'TICKET_TYPE_OFFER', 'TICKET_TYPE_TASK'],
      availableLinkTypes: ['TICKET_LINK_TYPE_RELATES', 'TICKET_LINK_TYPE_BLOCKS', 'TICKET_LINK_TYPE_IS_BLOCKED_BY'],
      availablePriority: ['DEFAULT', 'LOW', 'HIGH'],
      availableStatus: ['TICKET_STATUS_OPEN', 'TICKET_STATUS_IN_PROGRESS', 'TICKET_STATUS_DONE', 'TICKET_STATUS_CLOSED'],
      commentList: [] as TicketCommentListItem[],
      dates: {
        startDate: null as number | null,
        dueDate: null as number | null
      },
      countDocs: 0,
      countImg: 0,
      countVideo: 0,
      ticketDeleted: false,
      ticketClosed: false,
      history: [] as unknown[],
      historyLinkedTicket: [] as unknown[],
      historyReferences: [] as unknown[],
      historyLength: 0,
      orderData: ['assignee', 'onsite', 'operator', 'creator'],
      filteredMediaList: [] as TicketAttachmentMediaRow[],
      showDeclineTicketModal: false,
      descriptionDeclineTicket: '',
      declinedTicket: false,
      addTicketConnectionsModal: false,
      mainDevice: null as ObjectdeviceDevice | null,
      mainDeviceUninstallDateInput: '',
      newDevice: null as ObjectdeviceDevice | null,
      listTasks: [] as DetailsChecklistTask[],
      editingTaskNote: null as DetailsChecklistTask | null,
      editingTaskAssignee: null as DetailsChecklistTask | null,
      editChecklistAssigneeModalBool: false,
      newChecklistAssignee: null as string | null,
      availableStatusChecklist: ['CHECKLIST_ITEM_STATUS_OPEN', 'CHECKLIST_ITEM_STATUS_IN_PROGRESS', 'CHECKLIST_ITEM_STATUS_DONE'],
      showMergeOrdersModalBool: false,
      showUploadInvoiceModalBool: false,
      ownerDeepOEnabled: false,
      nodeProcessed: false,
      invoiceProcessing: false,
      initProcess: true, // we only sendFile when initProcess is set to false, to avoid calling sendFile when user decline/delete an invoice on deepO
      aicLoading: false,
      reportDamageInsuranceLoading: false,
      showReportDamageInsurance: false
    }
  },
  computed: {
    isOwnerDeepOEnabled() {
      return this.ownerDeepOEnabled
    },
    ticketStatus() {
      return this.ticket ? this.ticket.status : ''
    },
    isDeepOEnabled() {
      return this.$store.getters['user/getIsDeepOEnabled']
    },
    isFileTransmissionsProcessed() {
      // check if the latest (with highest created timestamp) file transmission has processed=true
      if (this.fileTransmissions && this.fileTransmissions.length > 0) {
        const latestFileTransmission = this.fileTransmissions.reduce((prev, current) => {
          return (prev.createdAt > current.createdAt) ? prev : current
        })
        return latestFileTransmission.processed
      }
      return false
    },
    linkedOrders(): TicketTicket[] {
      if (!this.ticket?.links?.length) {
        return []
      }
      const ticketIds = this.ticket.links.map(link => link.ticketId)
      return this.listTickets.filter(
        t => this.ticket!.links.length > 0 && t.type === 'TICKET_TYPE_ORDER' && ticketIds.includes(t.id)
      )
    },
    prevRoutePath() {
      return this.prevRoute ? this.prevRoute.path : '/'
    },
    prevRouteQuery() {
      return this.prevRoute ? this.prevRoute.query : null
    },
    orderMailOutboundEntry(): TicketMailEntry | null {
      const ticket = this.ticket
      if (!ticket || ticket.type !== 'TICKET_TYPE_ORDER' || !ticket.id) {
        return null
      }
      const arr = this.mailOutboundList
        .filter(x => x.referenceId?.includes(ticket.id!))
        .sort((a, b) => ((b.timestamp ?? 0) * 1000) - ((a.timestamp ?? 0) * 1000))
      return arr.length > 0 ? arr[0] : null
    },
    resultTicketsForLink(): { data: TicketTicket[] }[] {
      const key = this.ticket?.key
      return [{ data: this.ticketsForLink.filter(x => x && x.key && x.key !== key) }]
    },
    employeeList(): V2User[] {
      if (this.getEmployees().length > 0) {
        return this.getEmployees()
      }
      return []
    },
    listFilteredEmployee() {
      return this.employeeList.filter(x => (x.blocked === false && x.deleted === false))
    },
    countExternalWatchers() {
      return this.ticket.externalTicketWatchers?.filter(y => y.name !== '' && y.notifyOnlyStatusChanges).length || 0
    },
    hasEditRights() {
      // base edit permission before Ailean blocking
      return (this.ticket?.assignee?.userId === this.userId || this.ticket?.creator?.userId === this.userId) || this.isCompanyAdmin || this.isTicketEditor || false
    },
    canEditTicket() {
      // block editing for internal users when Ailean is the assignee
      if (this.isAileanAssigneeInternal) {
        return false
      }
      return this.hasEditRights
    },
    canEditStatus() {
      return this.canEditTicket || (this.publicTicket && this.publicTicket.status !== 'TICKET_STATUS_OPEN') || false
    },
    isInternalUser() {
      return !this.publicTicket && !this.orderTicket
    },
    isAileanTicket(): boolean {
      return isAileanTicket(this.ticket)
    },
    isAileanAssignee(): boolean {
      return isAileanUser(this.ticket?.assignee)
    },
    isAileanCreator(): boolean {
      return isAileanUser(this.ticket?.creator)
    },
    isAileanAssigneeInternal(): boolean {
      return this.isAileanAssignee && this.isInternalUser
    },
    canAddComment(): boolean {
      return (this.canEditTicket && (this.isEditor || this.isTicketCreator)) || !!this.publicTicket || (this.isAileanAssigneeInternal && (this.isEditor || this.isTicketCreator))
    },
    filteredReferences() {
      // this list is used to exclude connections (request / insurance / mortgage) for create order process
      return this.references.filter(x => !x.request && !x.insurance && !x.mortgage)
    },
    referencesDevices() {
      return JSON.parse(JSON.stringify(this.references.map(r => r.device).filter(d => d && !d.uninstallation)))
    },
    isAicTicket(): boolean {
      return !!(this.ticket?.additionalData?.some(data => data.key === 'aic' && data.value === 'true'))
    }
  },
  watch: {
    'ticket.invoiceDocumentId': function () {
      this.loadInvoiceDocument()
    },
    'ticket.invoiceDocument': function () {
      if (this.ticket.invoiceDocument && this.invoiceDocument === null) {
        this.loadInvoiceDocument()
      }
    },
    mailboxesForCurrentUser: function () {
      if (this.ticket?.id && this.ticket.id !== '') {
        this.loadMails([this.ticket.id])
      }
    },
    mediasList: function (newVal: TicketAttachmentMediaRow[]) {
      this.countDocs = 0
      const array = JSON.parse(JSON.stringify(newVal))
      array.sort((val1, val2) => {
        return parseInt(val2.uploadedAt) - parseInt(val1.uploadedAt)
      })
      array.forEach(element => {
        if (!(['image/jpeg', 'image/png', 'image/gif', 'video/webm', 'video/mp4'].indexOf(element.mimeType) !== -1)) {
          this.countDocs = this.countDocs + 1
        }
        if (['image/jpeg', 'image/png', 'image/gif'].indexOf(element.mimeType) !== -1) {
          this.countImg = this.countImg + 1
        }
        if (['video/webm', 'video/mp4'].indexOf(element.mimeType) !== -1) {
          this.countVideo = this.countVideo + 1
        }
      })
      this.filteredMediaList = array
    },
    tabIndex: function (newVal: number, oldVal: number) {
      if (this.hasUnsavedTicketEdits && this.oldValTab !== newVal && this.comment !== '') {
        this.$refs.confirmationModalTab.show()
        this.oldValTab = oldVal
      }
    },
    typeOfLink: function (newVal: string, oldVal: string) {
      this.hasUnsavedTicketEdits = true
      if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
        this.searchForTickets = ''
      }
    },
    employeeList: function (newVal: V2User[]) {
      if (newVal.length > 0) {
        this.getListTasks()
      }
    }
  },
  async mounted() {
    if (this.publicTicket) {
      DeepOApi.isDeepOEnabled(this.publicTicket.creator.accountId)
        .then(response => {
          this.ownerDeepOEnabled = response.enabled
        })
        .catch(e => {
          console.log(e)
        })
    }
    if (this.orderTicket !== null) {
      // ticket preview from create order process
      this.ticket = this.orderTicket
      if (this.ticket?.id && this.ticket.id !== '') {
        this.loadMails([this.ticket.id])
      }
      this.loadTicketData()
    } else if (this.id && this.id !== '') {
      // ticket preview from ticket list
      this.getTicketByKey(this.id)
    } else {
      // ticket preview from public ticket
      if (localStorage.getItem('unregisteredName' + this.email) === null) {
        this.$refs['unregistered-name-modal'].show()
      } else {
        this.unregisteredName = localStorage.getItem('unregisteredName' + this.email)
      }
      this.orderData = ['assignee', 'onsite', 'creator', 'operator']
      this.ticket = this.publicTicket
      this.loadTicketData()
      this.getTicketHistory(this.ticket.id, this.ticket.publicId)
      this.$store.dispatch('internationalization/changeCurrentLanguage', ['en', 'de', 'it'].includes(window.navigator.language.substring(0, 2)) ? window.navigator.language.substring(0, 2) : 'de').then()
      // filter status closed if public ticket
      if (this.publicTicket) {
        this.availableStatus = ['TICKET_STATUS_OPEN', 'TICKET_STATUS_IN_PROGRESS', 'TICKET_STATUS_DONE']
      }
      if (this.publicTicket.status === 'TICKET_STATUS_CLOSED') {
        this.ticketClosed = true
      }
      if (this.publicTicket) {
        const list = localStorage.getItem('listDeclinedTickets') ? JSON.parse(localStorage.getItem('listDeclinedTickets')) : []
        this.declinedTicket = list.length > 0 && list.includes(this.publicTicket.publicId)
      }
    }
    this.loadEmployees()
    if (this.isAuthenticated) {
      this.getContacts()
    }
    if (this.orderTicket !== null) {
      this.getListTasks()
    }
    await this.checkAicEnabled()
  },
  methods: {
    getDeviceTypeLabel(type: string): string {
      const translated = String(this.$t('message.onBoarding.devices.typeValues.' + type))
      return translated.length > 50 ? translated.slice(0, 50) + '...' : translated
    },
    autosuggestTicketRows(suggestion: unknown): TicketTicket[] {
      const t = this.autosuggestSuggestionToTicket(suggestion)
      return t ? [t] : []
    },
    autosuggestSuggestionToTicket(suggestion: unknown): TicketTicket | null {
      if (suggestion && typeof suggestion === 'object' && 'item' in suggestion) {
        const item = (suggestion as { item?: TicketTicket }).item
        return item ?? null
      }
      return null
    },
    formatTicketDateDisplay(value: unknown): string {
      const fn = this.$options.filters?.objectToDate as ((v: unknown) => string) | undefined
      return fn ? fn(value) : ''
    },
    formatCostShareChfDisplay(value: unknown): string {
      const fn = this.$options.filters?.formatPrice as ((v: unknown, currency: string) => string) | undefined
      return fn ? fn(value, 'CHF') : ''
    },
    getContactRouteId(contact: NonNullable<TicketReferenceView['contact']>): string {
      if ('objectID' in contact && contact.objectID) {
        return contact.objectID
      }
      return contact.id
    },
    ...mapActions('employee', ['loadEmployees']),
    ...mapGetters('employee', ['getEmployees']),
    ...mapMutations('message', [
      'setSendMailContacts',
      'setEmailToForwardAsAttachment',
      'setSendMailPreselectedContact',
      'setSendMailReferenceIds',
      'setSendMailEmailToReply',
      'setSendMailCC',
      'setSendMailOnlyCurrentContact',
      'setSendMailHasContext',
      'setMailsLoadedAt',
      'setChangingStatusMails',
      'resetStatusMails',
      'setSubject'
    ]),
    formatCostAbsorption(costAbsorption) {
      if (!costAbsorption || costAbsorption === 'COST_ABSORPTION_UNDEFINED') {
        return ''
      }
      return this.$t('message.ticketOverview.ticket.costs.costAbsorptionValues.' + costAbsorption)
    },
    formatCostShareValue(entry) {
      if (!entry || entry.costShareUnit === 'COST_SHARE_UNIT_UNDEFINED' || entry.costShareValue == null) {
        return ''
      }
      if (entry.costShareUnit === 'COST_SHARE_UNIT_PERCENT') {
        return entry.costShareValue + '%'
      }
      return this.$options.filters.formatPrice(entry.costShareValue, 'CHF')
    },
    startProcessingInvoice() {
      if (this.publicTicket) {
        this.finishOrderMethod()
        return
      }
      if (!this.invoiceProcessing) {
        // this.invoiceProcessing = true
        this.loadingAnimation = true
        this.nodeProcessed = false
        this.initProcess = true
        this.processInvoiceMethod()
        Vue.toasted.show(this.$t('message.ticketOverview.ticket.uploadInvoice.deepOOpen'), { type: 'success' })
      }
    },
    async processInvoiceMethod(loopIndex = 0) {
      loopIndex += 1
      const delay = this.getDelayByIndex(loopIndex)
      if (!delay) {
        return
      }
      if (this.publicTicket) {
        this.finishOrderMethod()
        return
      }
      if (this.fileTransmissions && this.fileTransmissions.length > 0) {
        this.initProcess = false
        // Use the latest file transmission (with highest created timestamp) if there are multiple
        const latestFileTransmission = this.fileTransmissions.reduce((prev, current) => {
          return (prev.createdAt > current.createdAt) ? prev : current
        })
        if (!this.nodeProcessed) {
          await this.analyseNodeStatus(latestFileTransmission.id)
          if (this.nodeProcessed) {
            await this.getFileTransmissions()
            // If all files in the node are already processed, open uploaded file
            // temporary timeout, because analyseNodeStatus return always true
            this.loadingAnimation = false
            Vue.toasted.show(this.$t('message.ticketOverview.ticket.uploadInvoice.invoiceSent'), { type: 'success' })
            window.open(latestFileTransmission.editorUrl, '_blank')
            setTimeout(
              () => {
                this.processInvoiceMethod()
              }, 8000
            )
          } else {
            setTimeout(
              () => {
                this.processInvoiceMethod(loopIndex)
              }, delay
            )
          }
        } else if (!latestFileTransmission.processed) {
          await this.getFileTransmissions()
          setTimeout(
            () => {
              this.processInvoiceMethod(loopIndex)
            }, delay
          )
        }
      } else if (this.initProcess === true) {
        // Send the latest version of the invoice document to sendFile
        const lastVersion = this.invoiceDocument.versions[this.invoiceDocument.versions.length - 1]
        DeepOApi.sendFile(lastVersion.mediaId, [this.ticket.id])
          .then(response => {
            this.fileTransmissions = this.fileTransmissions.concat(response.fileTransmission)
            if (response.fileTransmission && response.fileTransmission.processed) {
              this.loadingAnimation = false
              Vue.toasted.show(this.$t('message.ticketOverview.ticket.uploadInvoice.invoiceSent'), { type: 'success' })
              window.open(response.fileTransmission.editorUrl, '_blank')
            } else {
              if (response.fileTransmission) {
                setTimeout(
                  () => {
                    this.processInvoiceMethod()
                  }, 1000
                )
              }
            }
        })
          .catch(e => {
          this.loadingAnimation = false
          this.invoiceProcessing = false
          console.log(e)
          Vue.toasted.show(this.$t('message.ticketOverview.ticket.uploadInvoice.invoiceFailedToSent'), { type: 'error' })
        })
      }
    },
    getDelayByIndex(loopIndex) {
      // First 1 minutes → every 3 second
      if (loopIndex < 20) {
        return 3000
      }

      // Next 9 minutes → every 6 seconds
      if (loopIndex < 200 + 90) {
        return 6000
      }

      // Next 50 minutes → every 30 seconds
      if (loopIndex < 200 + 90 + 100) {
        return 30000
      }

      // After schedule ends → stop
      return null
    },
    loadInvoiceDocument() {
      if (this.ticket && this.ticket.invoiceDocumentId && this.ticket.invoiceDocumentId !== '') {
        if (this.publicTicket && this.publicTicket.invoiceDocument) {
          this.invoiceDocument = this.publicTicket.invoiceDocument
        } else {
          MediaApi.listDocuments([this.ticket.invoiceDocumentId])
            .then(response => {
              this.invoiceDocument = response?.documents[0]
            })
        }
        if (!this.publicTicket) {
          this.getFileTransmissions()
        }
      }
    },
    getFileTransmissions() {
      return DeepOApi.getFileTransmissionsByReferenceId(this.ticket.id)
        .then(response => {
          this.fileTransmissions = response.fileTransmissions
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.message'), { type: 'error' })
        })
    },
    analyseNodeStatus(fileTransmissionId) {
      return DeepOApi.analyseNodeStatus(fileTransmissionId)
        .then(response => {
          this.nodeProcessed = response.finished
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.message'), { type: 'error' })
        })
    },
    versionAdded(updatedDocument) {
      this.invoiceDocument = updatedDocument
    },
    deleteDocument() {
      this.ticket.invoiceDocumentId = ''
      this.invoiceDocument = null
      DeepOApi.deleteInvoice(this.ticket.id, { publicId: this.publicTicket ? this.publicTicket.publicId : this.unregisteredName !== '' ? this.ticket.publicId : '', operatorName: this.publicTicket ? this.unregisteredName : '' })
        .then(() => {
          this.invoiceProcessing = false
          Vue.toasted.show(this.$t('message.successMessages.invoiceDeleted'), { type: 'success' })
        })
        .catch(e => {
          this.invoiceProcessing = false
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.message'), { type: 'error' })
        })
    },
    editTaskNote(task) {
      this.editingTaskNote = { ...task }
    },
    saveTaskNote(e) {
      // Prevent new line on Enter in textarea for task note
      if (e && e.preventDefault) {
        e.preventDefault()
      }
      if (this.orderTicket === null) {
        this.updateTicket('checklistNote')
      } else {
        const task = this.listTasks.find(x => x.id === this.editingTaskNote.id)
        this.$emit('checklist-note-changed', task)
      }
      this.editingTaskNote = null
    },
    saveTaskStatus(task) {
      if (this.orderTicket === null) {
        this.updateTicket('checklistStatus')
      } else {
        this.$emit('checklist-status-changed', task)
      }
    },
    hideEditChecklistAssigneeModal() {
      this.$refs.editChecklistAssigneeModal.hide()
      this.editChecklistAssigneeModalBool = false
      this.newChecklistAssignee = null
      this.editingTaskAssignee = null
    },
    showEditChecklistAssigneeModal(task) {
      this.editChecklistAssigneeModalBool = true
      this.newChecklistAssignee = task.assignee ? this.employeeList.find(x => x.id === task.assignee.id).id : null
      this.editingTaskAssignee = { ...task }
      this.$nextTick(() => {
        this.$refs.editChecklistAssigneeModal.show()
      })
    },
    saveNewAssigneeChecklist() {
      if (this.editingTaskAssignee) {
        const task = this.listTasks.find(x => x.id === this.editingTaskAssignee.id)
        const assignee = this.newChecklistAssignee ? this.employeeList.find(x => x.id === this.newChecklistAssignee) : null
        if (task) {
          task.assignee = assignee
          const index = this.listTasks.findIndex(x => x.id === task.id)
          this.listTasks.splice(index, 1, task)
          if (this.orderTicket === null) {
            this.updateTicket('checklistAssignee')
          } else {
            this.$emit('checklist-assignee-changed', this.newChecklistAssignee, task.id)
          }
          this.hideEditChecklistAssigneeModal()
        }
      }
    },
    getListTasks() {
      if (this.ticket && this.ticket.type === 'TICKET_TYPE_CHECKLIST' && this.ticket.checklist?.length > 0) {
        const checklist = JSON.parse(JSON.stringify(this.ticket.checklist))
        this.listTasks = checklist.map((item) => {
          const assignee = item.assignee ? this.employeeList.find(x => x.id === item.assignee.userId) : null
          delete item.assignee
          return {
            ...item,
            assignee: assignee
          }
        })
      }
    },
    getWatchersNames() {
      const names = this.ticket.externalTicketWatchers.filter(y => y.name !== '' && y.notifyOnlyStatusChanges).map(x => x.name)
      if (names.length > 0) {
        let html = '<p class="text-left mb-0">'
        names.forEach(name => {
          html += name + '<br>'
        })
        html += '</p>'
        return html
      }
      return ''
    },
    loadMails(referenceIds, mailboxIds = this.getMailboxesForUserId(this.userId, false).map(mailbox => mailbox.id)) {
      if (mailboxIds?.length > 0 && referenceIds.length > 0) {
        MessageApi.listMailsByReferenceIds(referenceIds, 'FILTER_FOLDER_ALL', mailboxIds, 1, null, '')
          .then(async response => {
            this.mailList = response.mails.filter(x => x.mailType !== 'MAIL_TYPE_TICKET_NOTIFICATION') as TicketLinkedMailRow[]
          }).catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.message'), { type: 'error' })
        })
      }
      if (referenceIds.length > 0) {
        MessageApi.getDeliveryAndEngagementData(referenceIds)
          .then(response => {
            this.mailOutboundList = response.entries as TicketMailEntry[]
          }).catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.message'), { type: 'error' })
        })
      }
    },
    getContacts() {
      ContactApi.getContacts(this.$store.getters['user/getAccountId'])
        .then(response => {
          this.contactList = [...response.persons, ...response.companies] as Contact[]
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.contacts'), { type: 'error' })
        })
    },
    markAsUnread() {
      TicketApi.markUnread([this.ticket.id])
        .then(() => {
          this.getCountMyUnreadTickets(true)
          Vue.toasted.show(this.$t('message.successMessages.markTicketUnread'), { type: 'success' })
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.loadingErrors.ticket'), { type: 'error' })
        })
    },
    async getTicketByKey(key) {
      await TicketApi.getTicketByKey(key)
        .then(response => {
          this.ticket = response.ticket
          if (this.ticket.readStatus && !this.ticket.readStatus.assignee && this.ticket.assignee && this.ticket.assignee.userId === this.$store.getters['user/getUserId']) {
            TicketApi.markRead([this.ticket.id])
              .then(() => {
                // this.$parent.$refs['coozzy-header'].getCountMyUnreadTickets()
                this.getCountMyUnreadTickets(true)
              })
              .catch(e => {
                console.log(e)
                Vue.toasted.show(this.$t('message.loadingErrors.ticket'), { type: 'error' })
              })
          }
          this.loadMails([this.ticket.id])
          this.getTicketHistory(this.ticket.id)
          this.loadTicketData()
          this.getListTasks()
        })
        .catch(e => {
          if (e.response) {
            const error = e.response.data
            if (error.message === 'ticket not found') {
              this.ticketDeleted = true
            } else {
              console.log(e)
              Vue.toasted.show(this.$t('message.loadingErrors.ticket'), { type: 'error' })
            }
          } else {
            console.log(e)
            Vue.toasted.show(this.$t('message.loadingErrors.ticket'), { type: 'error' })
          }
        })
    },
    getRoomName(list, id) {
      let name = ''
      list.relatedReference?.forEach((element) => {
        if (element && element.id === id) {
          name = element.name + ', '
        }
      })
      return name
    },
    documentPreview(url, renderId) {
      if (url !== '') {
        window.open(url, '_blank')
      } else {
        if (this.orderTicket.listAttachment.find(e => e.renderId === renderId)?.loading === false) {
          this.generateTemplates(renderId)
        }
      }
    },
    generateTemplates(renderId) {
      this.orderTicket.listAttachment.forEach(element => {
        if (element.renderId === renderId) {
          element.loading = true
        }
      })
      const recheckDocs = setInterval(() => {
        ObjectApi.listRenderStatus([renderId])
          .then(response => {
            const stillInProgress = response.status.filter(function (v) {
              return v.finished === false && v.failed === false
            })
            if (stillInProgress.length === 0) {
              const isFinished = response.status.every(function (v) {
                return v.finished !== false || v.failed !== false
              })
              if (isFinished) {
                const onlyFinished = response.status.filter(x => x.finished)
                const onlyFailed = response.status.filter(x => x.failed)
                clearInterval(recheckDocs)
                if (onlyFailed.length > 0) {
                  this.orderTicket.listAttachment.forEach(element => {
                    if (element.renderId === renderId) {
                      element.loading = false
                    }
                  })
                  Vue.toasted.show(this.$t('message.savingErrors.renderingContractError') + onlyFailed.map(x => x.renderId).toString() + ' ' + onlyFailed.map(x => x.errorMessage).toString(), { type: 'error' })
                }
                if (onlyFinished.length > 0) {
                  ObjectApi.getRenderFile(renderId)
                    .then(element => {
                      // decode base64 string
                      const binary = window.atob(element.contract)
                      const len = binary.length
                      const buffer = new ArrayBuffer(len)
                      const view = new Uint8Array(buffer)
                      for (let i = 0; i < len; i++) {
                        view[i] = binary.charCodeAt(i)
                      }
                      let blob: Blob
                      blob = new Blob([view], { type: 'application/pdf' })
                      const link = document.createElement('a')
                      link.href = window.URL.createObjectURL(blob)
                      this.orderTicket.listAttachment.forEach(element => {
                        if (element.renderId === renderId) {
                          element.url = link.href
                          element.loading = false
                          element.size = blob.size
                          element.blob = blob
                        }
                      })
                      window.open(link.href, '_blank')
                    })
                }
              }
            }
          })
      }, 3000)
    },
    downloadFiles(media, e) {
      const mimeType = media.media ? media.media.mimeType : media.mimeType
      if (mimeType === 'application/pdf') {
        e.preventDefault()
        const url = media.media ? media.media.url : media.url
        const fileName = media.media ? media.media.filename : media.filename
        saveAs(url, fileName)
      }
    },
    getTicketHistory(id, publicId = '') {
      TicketApi.getTicketHistory(id, publicId).then(response => {
        this.history = response.events.reverse()
        this.historyLength = 0
        const promises = []
        let listLinkedTicket = []
        const listConnections = []
        this.history.forEach(element => {
          if (element.historyType === 'HISTORY_TYPE_ADDED_CONNECTIONS') {
            if (element.connectionAdded?.references?.length > 0) {
              element.connectionAdded?.references.forEach(element => {
                listConnections.push(element)
              })
            }
          }
          if (element.historyType === 'HISTORY_TYPE_REMOVED_CONNECTIONS') {
            if (element.connectionRemoved?.references?.length > 0) {
              element.connectionRemoved?.references.forEach(element => {
                listConnections.push(element)
              })
            }
          }
          if (element.historyType === 'HISTORY_TYPE_ADDED_LINKS') {
            if (element.linksAdded?.links?.length > 0) {
              listLinkedTicket = listLinkedTicket.concat(element.linksAdded?.links.map(x => x.ticketId))
            }
          }
          if (element.historyType === 'HISTORY_TYPE_REMOVED_LINKS') {
            if (element.linksRemoved?.links?.length > 0) {
              listLinkedTicket = listLinkedTicket.concat(element.linksRemoved?.links.map(x => x.ticketId))
            }
          }

          if (element.historyType !== 'HISTORY_TYPE_NEW_COMMENT' && element.historyType !== 'HISTORY_TYPE_EDITED_COMMENT' && element.historyType !== 'HISTORY_TYPE_DELETED_COMMENT') {
            this.historyLength++
          } else {
            if (element.commentNew !== null || element.commentEdited !== null || element.commentDeleted !== null) {
              this.historyLength++
            }
          }
        })
        listLinkedTicket = listLinkedTicket.map((x) => {
          return x.substring(x.indexOf('ticket'))
        })
        this.historyReferences = this.mountedReference(listConnections)
        if (listLinkedTicket.length > 0 && !this.publicTicket) {
          promises.push(TicketApi.getTicketByIds(listLinkedTicket))
          if (promises.length > 0) {
            Promise.all(promises)
              .then(response => {
                response.forEach(element => {
                  if (element?.tickets?.length > 0) {
                    this.historyLinkedTicket = element.tickets
                  }
                })
              })
          }
        }
      }).catch(e => {
        console.log(e)
      })
    },
    openVideo(media) {
      this.selectedObjectVideo = media.url
      this.$nextTick(() => {
        this.$refs['show-video'].show()
      })
    },
    itemChanged(selected: unknown) {
      const s = selected as { item?: TicketTicket } | null
      if (s?.item) {
        this.searchForTickets = s.item.key + ' ' + s.item.title
      }
    },
    getSuggestionValue(suggestion: unknown) {
      const t = this.autosuggestSuggestionToTicket(suggestion)
      return t ? t.key + ' ' + t.title : ''
    },
    checkIsDisabled(item: TicketTicket | null) {
      if (!item) {
        return true
      }
      if (this.typeOfLink) {
        if (this.typeOfLink === 'TICKET_LINK_TYPE_IS_BLOCKED_BY' && this.filteredTicketLinked('TICKET_LINK_TYPE_IS_BLOCKED_BY').length > 0) {
          for (const ticket of this.filteredTicketLinked('TICKET_LINK_TYPE_IS_BLOCKED_BY')) {
            if (item.id === ticket.ticketId) {
              return true
            }
          }
        } else if (this.typeOfLink === 'TICKET_LINK_TYPE_BLOCKS' && this.filteredTicketLinked('TICKET_LINK_TYPE_BLOCKS').length > 0) {
          for (const ticket of this.filteredTicketLinked('TICKET_LINK_TYPE_BLOCKS')) {
            if (item.id === ticket.ticketId) {
              return true
            }
          }
        } else if (this.typeOfLink === 'TICKET_LINK_TYPE_RELATES' && this.filteredTicketLinked('TICKET_LINK_TYPE_RELATES').length > 0) {
          for (const ticket of this.filteredTicketLinked('TICKET_LINK_TYPE_RELATES')) {
            if (item.id === ticket.ticketId) {
              return true
            }
          }
        } else {
          return false
        }
      }
      return false
    },
    getMediaUrl(media) {
      if (media.thumbnails === undefined || media.thumbnails === null || typeof media.thumbnails === 'undefined') {
        return null
      }
      for (const thumbnail of media.thumbnails) {
        if (thumbnail.type === 1 || thumbnail.type === 'M') {
          return thumbnail.url
        }
      }
      return media.url
    },
    getTicketDetailRoute(ticket) {
      if (this.isOwnerModule) {
        return {
          name: `OwnerTicket${ticket.status === 'TICKET_STATUS_DRAFT' ? 'Edition' : 'DetailsView'}`,
          params: { id: ticket.key }
        }
      } else if (this.isAdminModule) {
        return {
          name: `AdminTicket${ticket.status === 'TICKET_STATUS_DRAFT' ? 'Edition' : 'DetailsView'}`,
          params: { id: ticket.key }
        }
      } else if (this.isAssetModule) {
        return {
          name: `AssetTicket${ticket.status === 'TICKET_STATUS_DRAFT' ? 'Edition' : 'DetailsView'}`,
          params: { id: ticket.key }
        }
      } else if (this.isAccountingModule) {
        return {
          name: `AccountingTicket${ticket.status === 'TICKET_STATUS_DRAFT' ? 'Edition' : 'DetailsView'}`,
          params: { id: ticket.key }
        }
      } else {
        return {
          name: `MarketingTicket${ticket.status === 'TICKET_STATUS_DRAFT' ? 'Edition' : 'DetailsView'}`,
          params: { id: ticket.key }
        }
      }
    },
    onLinkedTicketClick(event: MouseEvent, ticket: TicketTicket | undefined) {
      if (!ticket) {
        return
      }
      if (ticket.status === 'TICKET_STATUS_DRAFT' && ticket.type === 'TICKET_TYPE_ORDER') {
        event.preventDefault()
        this.openDraftOrder(ticket)
      }
    },
    async openDraftOrder(item: TicketTicket) {
      const currentRoute = { name: this.$router.currentRoute.name, params: this.$router.currentRoute.params, query: this.$router.currentRoute.query }
      localStorage.setItem('LS_ROUTE_KEY', JSON.stringify(currentRoute))

      const refs = item.references || []
      const propertyIds: string[] = []
      const buildingIds: string[] = []
      const objectIds: string[] = []
      const tenancyIds: string[] = []
      const condoOwnerIds: string[] = []
      const deviceIds: string[] = []
      const contactIds: string[] = []
      const roomIds: string[] = []
      const categorizeId = (id: string) => {
        switch (true) {
          case id.startsWith('prop_'):
            if (!propertyIds.includes(id)) propertyIds.push(id)
            break
          case id.startsWith('building_'):
            if (!buildingIds.includes(id)) buildingIds.push(id)
            break
          case id.startsWith('obj_'):
            if (!objectIds.includes(id)) objectIds.push(id)
            break
          case id.startsWith('tenancy_'):
            if (!tenancyIds.includes(id)) tenancyIds.push(id)
            break
          case id.startsWith('condominium_'):
            if (!condoOwnerIds.includes(id)) condoOwnerIds.push(id)
            break
          case id.startsWith('device_'):
            if (!deviceIds.includes(id)) deviceIds.push(id)
            break
          case id.startsWith('contact_'):
            if (!contactIds.includes(id)) contactIds.push(id)
            break
          case id.startsWith('room_'):
            if (!roomIds.includes(id)) roomIds.push(id)
            break
        }
      }
      refs.forEach((ref) => {
        categorizeId(ref.id)
        if (ref.relatedIds) {
          ref.relatedIds.forEach(id => categorizeId(id))
        }
      })

      const query: Record<string, string> = {
        draftTicketId: item.id,
        source: 'draftOrder'
      }
      if (propertyIds.length) query.propertyIds = propertyIds.join(',')
      if (buildingIds.length) query.buildingIds = buildingIds.join(',')
      if (objectIds.length) query.objectIds = objectIds.join(',')
      if (tenancyIds.length) query.tenancyIds = tenancyIds.join(',')
      if (condoOwnerIds.length) query.condoOwnerIds = condoOwnerIds.join(',')
      if (deviceIds.length) query.deviceIds = deviceIds.join(',')
      if (contactIds.length) query.contactIds = contactIds.join(',')
      if (roomIds.length) query.roomIds = roomIds.join(',')

      try {
        const mailboxIds = this.getMailboxesForUserId(this.userId, false).map(mailbox => mailbox.id)
        const response = await MessageApi.listMailsByReferenceIds([item.id], 'FILTER_FOLDER_ALL', mailboxIds, 1, 1)
        const mails = (response.mails || []).filter(x => x.mailType !== 'MAIL_TYPE_TICKET_NOTIFICATION')
        if (mails.length > 0) {
          query.source = 'email'
          query.emailsData = JSON.stringify({ id: mails[0].id, referenceIds: mails[0].referenceIds })
        }
      } catch (e) {
        console.log(e)
      }

      let routeName = ''
      if (this.isOwnerModule) {
        routeName = 'OwnerCreateOrderView'
      } else if (this.isAdminModule) {
        routeName = 'AdminCreateOrderView'
      } else if (this.isAssetModule) {
        routeName = 'AssetCreateOrderView'
      } else if (this.isAccountingModule) {
        routeName = 'AccountingCreateOrderView'
      } else {
        routeName = 'MarketingCreateOrderView'
      }
      this.$router.push({ name: routeName, query: query }, () => {})
    },
    openModalDeclineTicket() {
      this.showDeclineTicketModal = true
      this.$nextTick(() => {
        this.$refs.declineTicketModal.show()
      })
    },
    hideModalDeclineTicket() {
      this.$refs.declineTicketModal.hide()
      this.showDeclineTicketModal = false
    },
    confirmDeclineTicket() {
      const comment = this.$t('message.ticketOverview.status.declineOrder') + (this.descriptionDeclineTicket !== '' ? '\n' + this.descriptionDeclineTicket : '')
      const name = localStorage.getItem('unregisteredName' + this.email) ? localStorage.getItem('unregisteredName' + this.email) : ''
      NoteApi.newCommentUnregistered(this.$route?.query?.email || '', name, [this.ticket.id], comment, [])
        .then(async response => {
          this.commentAdded(response.note)
          this.hideModalDeclineTicket()
          if (!localStorage.getItem('listDeclinedTickets')) {
            localStorage.setItem('listDeclinedTickets', JSON.stringify([this.ticket.publicId]))
            this.declinedTicket = true
          } else {
            const list = JSON.parse(localStorage.getItem('listDeclinedTickets'))
            const index = list.findIndex(x => x === this.ticket.publicId)
            this.declinedTicket = true
            if (index === -1) {
              list.push(this.ticket.publicId)
              localStorage.setItem('listDeclinedTickets', JSON.stringify(list))
            }
          }
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.savingErrors.note'), { type: 'error' })
        })
    },
    changePublicTicketStatus(value) {
      this.ticket.status = value
      this.handleTicketStatusChanged(value)
    },
    finishOrderMethod() {
      if (this.isOwnerDeepOEnabled) {
        this.showUploadInvoiceModalBool = true
        this.$nextTick(() => {
          this.$refs.uploadInvoiceModal.show()
        })
      } else {
        this.proceedWithoutInvoice()
      }
    },
    proceedWithoutInvoice() {
      this.ticket.status = 'TICKET_STATUS_DONE'
      if (!this.publicTicket) {
        this.$nextTick(() => {
          this.updateTicket()
        })
      }
      this.handleTicketStatusChanged('TICKET_STATUS_DONE')
      this.loadTicketData()
    },
    handleTicketStatusChanged(value) {
      if (this.availableStatus.includes(value)) {
        this.updateTicket('status')
      }
    },
    showTicketUpdateError(e, action = '') {
      const err = e.response && e.response.data
      if (err && err.code === 2) {
        if (action === 'link' || action === 'removeLink') {
          Vue.toasted.show(this.$t('message.savingErrors.ticketToItSelf'), { type: 'error' })
        } else if (action === 'status' || action === 'invoiceStatus') {
          Vue.toasted.show(this.$t('message.savingErrors.operatorOfTicketHasNoEmail', {
            orderNumber: this.ticket.key
          }), { type: 'error' })
        } else if (err.message) {
          Vue.toasted.show(err.message, { type: 'error' })
        } else {
          Vue.toasted.show(this.$t('message.savingErrors.ticket'), { type: 'error' })
        }
      } else {
        Vue.toasted.show(this.$t('message.savingErrors.ticket'), { type: 'error' })
      }
    },
    loadTicketData() {
      if (this.ticket?.references) {
        this.references = this.mountedReference(this.ticket.references)
      }
      if (this.ticket?.links && this.ticket.links.length > 0 && !this.publicTicket) {
        this.listTickets = this.buildLinkedTicketPlaceholders(this.ticket.links)
        TicketApi.getTicketByIds(this.ticket.links.map(x => x.ticketId))
          .then(response => {
            this.listTickets = this.mergeLinkedTicketDetails(this.ticket.links, response?.tickets || [])
          })
      } else {
        this.listTickets = []
      }
      this.mediasList = []
      if (this.ticket?.mediaIds.length > 0) {
        MediaApi.getListMedia(this.ticket.mediaIds)
          .then(response => {
            const list = []
            for (const media of response.media) {
              list.push(media)
            }
            this.mediasList = list
          })
          .catch(() => {
            Vue.toasted.show(this.$t('message.loadingErrors.media'), { type: 'error' })
          })
      }
      if (this.ticket?.invoiceDocumentId && this.ticket.invoiceDocumentId !== '') {
        this.loadInvoiceDocument()
      }
      this.loadComments()
      this.loadEmployees()
    },
    showInAic() {
      this.aicLoading = true
      AicApi.getSsoUrl(LandingPageEnum.AIC_LANDING_PAGE_DAMAGES_DETAILS, this.ticket.id)
        .then(response => {
          window.open(response.url, '_blank')
        })
        .catch(() => {
          Vue.toasted.show(this.$t('message.loadingErrors.aicSsoUrl'), { type: 'error' })
        })
        .finally(() => {
          this.aicLoading = false
        })
    },
    showModalReportDamageInsurance() {
      this.showReportDamageInsurance = true
    },
    editTicket(source = '') {
      let query = {}
      if (source === 'changeOperator') {
        query = { source: 'changeOperator' }
      }
      if (this.isOwnerModule) {
        this.$router.replace({
          name: 'OwnerTicketEdition',
          params: { id: this.ticket.key },
          query: query
        }, () => {
        })
      } else if (this.isAdminModule) {
        this.$router.replace({
          name: 'AdminTicketEdition',
          params: { id: this.ticket.key },
          query: query
        }, () => {
        })
      } else if (this.isAssetModule) {
        this.$router.replace({
          name: 'AssetTicketEdition',
          params: { id: this.ticket.key },
          query: query
        }, () => {
        })
      } else if (this.isAccountingModule) {
        this.$router.replace({
          name: 'AccountingTicketEdition',
          params: { id: this.ticket.key },
          query: query
        }, () => {
        })
      } else {
        this.$router.replace({
          name: 'MarketingTicketEdition',
          params: { id: this.ticket.key },
          query: query
        }, () => {
        })
      }
    },
    filteredTicketLinked(type) {
      if (!this.ticket?.links || this.ticket.links.length === 0) {
        return []
      }
      return this.ticket.links.filter(x => x.type === type)
    },
    buildLinkedTicketPlaceholders(links = []) {
      const uniqueTicketIds = [...new Set((links || []).map(link => link.ticketId).filter(Boolean))]
      return uniqueTicketIds.map(ticketId => ({
        id: ticketId,
        key: ticketId,
        title: '',
        status: 'TICKET_STATUS_OPEN',
        priority: 'DEFAULT',
        type: 'TICKET_TYPE_TASK',
        assignee: null,
        links: []
      }))
    },
    mergeLinkedTicketDetails(links = [], tickets = []) {
      const fallbackById = this.buildLinkedTicketPlaceholders(links).reduce((acc, item) => {
        acc[item.id] = item
        return acc
      }, {})
      tickets.forEach(item => {
        if (item?.id) {
          fallbackById[item.id] = item
        }
      })
      return (links || [])
        .map(link => fallbackById[link.ticketId])
        .filter(Boolean)
    },
    redirectToContactDetail(contactId) {
      if (contactId !== null) {
        if (this.isOwnerModule) {
          return {
            name: 'OwnerContactDetailsView',
            params: { id: contactId }
          }
        } else if (this.isAssetModule) {
          return {
            name: 'AssetContactDetailsView',
            params: { id: contactId }
          }
        } else if (this.isAccountingModule) {
          return {
            name: 'AccountingContactDetailsView',
            params: { id: contactId }
          }
        } else if (this.isMarketingModule) {
          return {
            name: 'MarketingContactDetailsView',
            params: { id: contactId }
          }
        } else {
          return {
            name: 'AdminContactDetailsView',
            params: { id: contactId }
          }
        }
      }
    },
    redirectToBuildingDetail(buildingId) {
      if (this.isOwnerModule) {
        return {
          name: 'OwnerBuildingDetailsView',
          params: { id: buildingId },
          query: { view: 'building' }
        }
      } else if (this.isAssetModule) {
        return {
          name: 'AssetBuildingDetailsView',
          params: { id: buildingId },
          query: { view: 'building' }
        }
      } else if (this.isAccountingModule) {
        return {
          name: 'AccountingBuildingDetailsView',
          params: { id: buildingId },
          query: { view: 'building' }
        }
      } else if (this.isAdminModule) {
        return {
          name: 'AdminBuildingDetailsView',
          params: { id: buildingId },
          query: { view: 'building' }
        }
      }
    },
    redirectToPropertyDetail(propertyId) {
      if (this.isOwnerModule) {
        return { name: 'OwnerPropertyDetailsView', params: { id: propertyId } }
      } else if (this.isAdminModule) {
        return { name: 'AdminPropertyDetailsView', params: { id: propertyId } }
      } else if (this.isAssetModule) {
        return { name: 'AssetPropertyDetailsView', params: { id: propertyId } }
      } else if (this.isAccountingModule) {
        return { name: 'AccountingPropertyDetailsView', params: { id: propertyId } }
      }
    },
    redirectToDeviceDetail(referenceId, deviceId) {
      const refItem = this.references.find(x =>
        x.device && (x.device.id === deviceId || x.device.objectID === deviceId)
      )
      const query = buildDeviceDetailQuery(
        deviceId,
        this.buildDeviceFiltersFromTicketReference(refItem)
      )
      if (referenceId.startsWith('obj') || referenceId.startsWith('building')) {
        let id = null
        if (referenceId.startsWith('building')) {
          id = referenceId
        } else if (refItem) {
          id = refItem.building?.id
        }
        if (id && deviceId) {
          if (this.isOwnerModule) {
            return {
              name: 'OwnerBuildingDetailsView',
              params: { id: id },
              query
            }
          } else if (this.isAdminModule) {
            return {
              name: 'AdminBuildingDetailsView',
              params: { id: id },
              query
            }
          } else if (this.isAssetModule) {
            return {
              name: 'AssetBuildingDetailsView',
              params: { id: id },
              query
            }
          } else if (this.isAccountingModule) {
            return {
              name: 'AccountingBuildingDetailsView',
              params: { id: id },
              query
            }
          }
        }
      } else {
        const id = referenceId
        if (this.isOwnerModule) {
          return {
            name: 'OwnerPropertyDetailsView',
            params: { id: id },
            query
          }
        } else if (this.isAdminModule) {
          return {
            name: 'AdminPropertyDetailsView',
            params: { id: id },
            query
          }
        } else if (this.isAssetModule) {
          return {
            name: 'AssetPropertyDetailsView',
            params: { id: id },
            query
          }
        } else if (this.isAccountingModule) {
          return {
            name: 'AccountingPropertyDetailsView',
            params: { id: id },
            query
          }
        }
      }
    },
    setRoomId(source, id) {
      if (source === 'building') {
        localStorage.setItem('expandBuildingRoomId', id)
      } else if (source === 'object') {
        localStorage.setItem('expandObjectRoomId', id)
      }
    },
    openRoomConnection(ref, newTab = false) {
      if (!ref?.room || !this.canOpenRoomConnection(ref)) {
        return
      }
      this.navigateToRoom(ref.room, ref.room.id, newTab, {
        buildingId: ref.object?.buildingId || ref.building?.id || ref.building?.objectID,
        objectId: ref.object?.objectID || ref.object?.id ||
          (ref.room.referenceId?.startsWith('obj') ? ref.room.referenceId : undefined)
      })
    },
    canOpenRoomConnection(ref) {
      if (!ref?.room?.referenceId) {
        return false
      }
      if (ref.room.referenceId.startsWith('building')) {
        return true
      }
      if (ref.room.referenceId.startsWith('obj')) {
        return !!(ref.object?.buildingId || ref.building?.id || ref.building?.objectID)
      }
      return false
    },
    setMortgageId(source, id) {
      if (source === 'property') {
        localStorage.setItem('expandPropertyMortgageId', id)
      }
    },
    setInsuranceId(source, id) {
      if (source === 'building') {
        localStorage.setItem('expandBuildingInsuranceId', id)
      } else if (source === 'property') {
        localStorage.setItem('expandPropertyInsuranceId', id)
      }
    },
    redirectToDetail(buildingId, objectId, view) {
      if (this.isOwnerModule) {
        return {
          name: 'OwnerBuildingDetailsView',
          params: { id: buildingId },
          query: {
            view: view,
            elementSelected: objectId
          }
        }
      } else if (this.isAdminModule) {
        return {
          name: 'AdminBuildingDetailsView',
          params: { id: buildingId },
          query: {
            view: view,
            elementSelected: objectId
          }
        }
      } else if (this.isAssetModule) {
        return {
          name: 'AssetBuildingDetailsView',
          params: { id: buildingId },
          query: {
            view: view,
            elementSelected: objectId
          }
        }
      } else if (this.isAccountingModule) {
        return {
          name: 'AccountingBuildingDetailsView',
          params: { id: buildingId },
          query: {
            view: view,
            elementSelected: objectId
          }
        }
      }
    },
    showLinkBlock() {
      this.showLinkInputs = true
      const yOffset = -70
      const domRect = this.$el.querySelector('#linkedTickets')
      if (domRect) {
        const y = domRect.getBoundingClientRect().top + window.pageYOffset + yOffset

        window.scrollTo({
          top: y,
          behavior: 'smooth'
        })
      }
    },
    updateTicket(action = '', isPublic = false) {
      if (!this.ticket?.id) {
        return
      }
      if (action === 'link' && this.ticketToLink !== '') {
        this.ticket.links.push({
          ticketId: this.ticketToLink,
          type: this.typeOfLink
        })
        this.typeOfLink = 'TICKET_LINK_TYPE_RELATES'
        this.ticketToLink = ''
        this.searchForTickets = ''
      }

      const tasks = JSON.parse(JSON.stringify(this.listTasks))
      this.ticket.checklist = tasks.map(x => {
        const taskAssignee = x.assignee
        const assignee = taskAssignee ? { accountId: taskAssignee.accountId, userId: taskAssignee.id } : null
        delete x.assignee
        return {
          assignee: assignee,
          ...x
        }
      })

      let updateMask = null
      if (action === 'watcher') {
        updateMask = 'id,watchers'
      }
      if (this.publicTicket || isPublic) {
        const publicTicket = isPublic ? this.ticket : this.publicTicket
        TicketApi.changedStatusPublicTicket(publicTicket.id, this.email, this.unregisteredName, publicTicket.status)
          .then(() => {
            this.hasUnsavedTicketEdits = false
            this.$nextTick(() => {
              this.$emit('refresh-ticket-data')
            })
            Vue.toasted.show(this.$t('message.successMessages.ticket'), { type: 'success' })
          })
          .catch(e => {
            console.log(e)
            this.showTicketUpdateError(e, action || 'status')
          })
      }
      if ((action !== 'status' && action !== 'invoiceStatus' && this.publicTicket && !isPublic) || !this.publicTicket) {
        TicketApi.updateTicket(this.ticket, updateMask)
          .then(async response => {
            if (action === 'link') {
              if (this.ticket?.links.length > 0 && !this.publicTicket) {
                const listTicketResponse = await TicketApi.getTicketByIds(this.ticket.links.map(x => x.ticketId))
                this.listTickets = this.mergeLinkedTicketDetails(this.ticket.links, listTicketResponse?.tickets || [])
              }
              this.showLinkInputs = false
            } else {
              this.ticket = response.ticket
            }
            this.getListTasks()
            this.hasUnsavedTicketEdits = false
            if (action === 'link') {
              Vue.toasted.show(this.$t('message.successMessages.addLinkTicket'), { type: 'success' })
            } else if (action === 'removeLink') {
              Vue.toasted.show(this.$t('message.successMessages.removeLinkTicket'), { type: 'success' })
            } else if (action === 'checklistStatus') {
              Vue.toasted.show(this.$t('message.successMessages.checklistStatusUpdated'), { type: 'success' })
            } else {
              Vue.toasted.show(this.$t('message.successMessages.ticket'), { type: 'success' })
            }
          })
          .catch(e => {
            console.log(e)
            this.showTicketUpdateError(e, action)
          })
      }
    },
    confirmUnregisteredName() {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        localStorage.setItem('unregisteredName' + this.email, this.unregisteredName)
        this.$refs['unregistered-name-modal'].hide()
      }
    },
    confirmCancellation() {
      this.$refs.confirmationModalTab.hide()
      this.comment = ''
      this.hasUnsavedTicketEdits = false
      this.oldValTab = this.tabIndex
    },
    cancelCancellation() {
      this.$refs.confirmationModalTab.hide()
      this.tabIndex = this.oldValTab
      this.$nextTick(function () {
        if (this.$refs.commentBlock) {
          this.$refs.commentBlock.commentInputEnabled = true
        }
        this.$el?.querySelector('#block-comment textarea')?.focus()
      })
    },
    removeTicketLinked() {
      this.ticket.links = this.ticket.links.filter(x => !(x.ticketId === this.removeLinkedTicket.ticketId && x.type === this.removeLinkedTicket.type))
      this.updateTicket('removeLink')
      this.removeLinkedTicket = null
      this.$refs.confirmationModalRemoveLinkedTicket.hide()
    },
    removeTicketLinkedClicked(ticket) {
      this.removeLinkedTicket = ticket
      this.$refs.confirmationModalRemoveLinkedTicket.show()
    },
    confirmDeleteTicket() {
      this.$bvModal.show('confirmationModalDeleteTicket')
    },
    showUploadInvoiceModal() {
      this.showUploadInvoiceModalBool = true
      this.$nextTick(() => {
        this.$refs.uploadInvoiceModal?.show()
      })
    },
    hideUploadInvoiceModal() {
      this.showUploadInvoiceModalBool = false
       this.$nextTick(() => {
        this.$refs.uploadInvoiceModal?.hide()
      })
    },
    uploadedInvoice(ticket, isPublic = false) {
      if (!isPublic || (isPublic && (this.ticket.status === 'TICKET_STATUS_IN_PROGRESS' || this.ticket.status === 'TICKET_STATUS_OPEN'))) {
        this.ticket = ticket
        // Ticket status changes to "Done"
        // this.ticket.status = 'TICKET_STATUS_DONE'
        // this.$nextTick(() => {
        //   this.updateTicket('invoiceStatus', isPublic)
        // })
      }
      if (isPublic) {
        this.$emit('refresh-ticket-data')
      }
      this.loadTicketData()
    },
    showMergeOrdersModal() {
      this.showMergeOrdersModalBool = true
      this.$nextTick(function () {
        this.$refs.mergeOrdersModal?.show()
      })
    },
    hideMergeOrdersModal() {
      this.showMergeOrdersModalBool = false
    },
    mergeOrders(listOrders) {
        const currentRoute = { name: this.$router.currentRoute.name, params: this.$router.currentRoute.params, query: this.$router.currentRoute.query }
        localStorage.setItem('LS_ROUTE_KEY', JSON.stringify(currentRoute))
        const refs = listOrders.flatMap(entry => entry.references)
        const ticketIds = listOrders.map(x => x.id).join(',')
        let propertyIds = []
        let buildingIds = []
        let objectIds = []
        let tenancyIds = []
        let condoOwnerIds = []
        let deviceIds = []
        let contactIds = []
        let roomIds = []
        refs.forEach((item) => {
          switch (true) {
            case item.id.startsWith('prop_'):
              propertyIds.push(item.id)
              break
            case item.id.startsWith('building_'):
              buildingIds.push(item.id)
              break
            case item.id.startsWith('obj_'):
              objectIds.push(item.id)
              break
            case item.id.startsWith('tenancy_'):
              tenancyIds.push(item.id)
              break
            case item.id.startsWith('condominium_'):
              condoOwnerIds.push(item.id)
              break
            case item.id.startsWith('device_'):
              deviceIds.push(item.id)
              break
            case item.id.startsWith('contact_'):
              contactIds.push(item.id)
              break
            case item.id.startsWith('room_'):
              roomIds.push(item.id)
              break
          }
        })
        const query = {
          ticketIds: ticketIds,
          propertyIds: propertyIds.join(','),
          buildingIds: buildingIds.join(','),
          objectIds: objectIds.join(','),
          tenancyIds: tenancyIds.join(','),
          condoOwnerIds: condoOwnerIds.join(','),
          deviceIds: deviceIds.join(','),
          contactIds: contactIds.join(','),
          roomIds: roomIds.join(','),
          source: 'mergeOrders',
          mainTicketForMerge: this.ticket.id
        }
        if (this.isOwnerModule) {
          this.$router.push({ name: 'OwnerCreateOrderView', query: query }, () => {})
        } else if (this.isAdminModule) {
          this.$router.push({ name: 'AdminCreateOrderView', query: query }, () => {})
        } else if (this.isAssetModule) {
          this.$router.push({ name: 'AssetCreateOrderView', query: query }, () => {})
        } else if (this.isAccountingModule) {
          this.$router.push({ name: 'AccountingCreateOrderView', query: query }, () => {})
        }
    },
    showAddTicketConnectionsModal() {
      this.addTicketConnectionsModal = true
      this.$nextTick(function () {
        this.$refs.addTicketConnectionsModal?.show()
      })
    },
    closeAddTicketConnectionsModal() {
      this.addTicketConnectionsModal = false
    },
    deleteTicket() {
      TicketApi.deleteTicket(this.ticket.id)
        .then(() => {
          this.redirectToOverview()
          Vue.toasted.show(this.$t('message.successMessages.deleteTicket'), { type: 'success' })
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.deleteErrors.ticket'), { type: 'error' })
        })
    },
    loadComments() {
      if (this.ticket.id) {
        NoteApi.getCommentByReferenceIds([this.ticket.id])
          .then(async response => {
            this.commentList = response.notes as TicketCommentListItem[]
          })
          .catch(e => {
            console.log(e)
            Vue.toasted.show(this.$t('message.loadingErrors.events'), { type: 'error' })
          })
      }
    },
    redirectToOverview() {
      let query = null
      if (this.prevRoutePath.includes('/tickets/')) {
        query = this.prevRouteQuery
      }
      if (this.isOwnerModule) {
        this.$router.push({ name: 'OwnerTicketOverview', query: query }, () => {
        })
      } else if (this.isAdminModule) {
        this.$router.push({ name: 'AdminTicketOverview', query: query }, () => {
        })
      } else if (this.isAssetModule) {
        this.$router.push({ name: 'AssetTicketOverview', query: query }, () => {
        })
      } else if (this.isAccountingModule) {
        this.$router.push({ name: 'AccountingTicketOverview', query: query }, () => {
        })
      } else {
        this.$router.push({ name: 'MarketingTicketOverview', query: query }, () => {
        })
      }
    },
    commentAdded(note: TicketCommentListItem) {
      this.commentList.unshift(note)
      this.hasUnsavedTicketEdits = false
    },
    commentUpdated(note: TicketCommentListItem) {
      this.commentList.forEach((value, index) => {
        if (value.id === note.id) {
          Vue.set(this.commentList, index, note)
        }
      })
      this.hasUnsavedTicketEdits = false
    },
    commentDeleted(note: TicketCommentListItem) {
      const index = this.commentList.findIndex(c => c.id === note.id)
      Vue.delete(this.commentList, index)
    },
    watchComment(value) {
      this.hasUnsavedTicketEdits = value
    },
    commentEdited(value) {
      this.comment = value
    },
    getDateTimeFormatted(timestampInSeconds) {
      return this.$options.filters.timestampToDateTime(timestampInSeconds)
    },
    backClicked() {
      if (this.prevRoutePath.includes('tickets/edit') || this.prevRoutePath.includes('tickets/create')) {
        if (this.isOwnerModule) {
          this.$router.push({ name: 'OwnerTicketOverview' }, () => {
          })
        } else if (this.isAdminModule) {
          this.$router.push({ name: 'AdminTicketOverview' }, () => {
          })
        } else if (this.isAssetModule) {
          this.$router.push({ name: 'AssetTicketOverview' }, () => {
          })
        } else if (this.isAccountingModule) {
          this.$router.push({ name: 'AccountingTicketOverview' }, () => {
          })
        } else {
          this.$router.push({ name: 'MarketingTicketOverview' }, () => {
          })
        }
      } else {
        this.$router.go(-1)
      }
    },
    redirectToRequestDetail(objectId, requestId) {
      return {
        name: 'MarketingObjectDetailsView',
        params: { id: objectId },
        query: {
          view: 0,
          elementSelected: requestId
        }
      }
    },
    getTickets() {
      TicketApi.ListTicketByQuery(this.searchForTickets)
        .then(response => {
          this.ticketsForLink = response.tickets
        })
    },
    formatDateForCalendar(date, time) {
      // start and end need to be in format the calendar package can understand
      return `${date.day.toString().padStart(2, '0')}.${date.month.toString().padStart(2, '0')}.${date.year}` +
        ` ${time.hours.toString().padStart(2, '0')}:${time.minutes.toString().padStart(2, '0')}`
    },
    imageClicked(item) {
      if (item.url && item.url !== '') {
        this.galleryImages = [
          item.url
        ]
        this.$nextTick(function () {
          this.$viewerApi({
            images: this.galleryImages,
            options: this.viewerOptions
          })
        })
      }
    },
    onSelectLinked(selected: unknown) {
      const s = selected as { item?: TicketTicket } | null
      if (s?.item) {
        if (!this.checkIsDisabled(s.item)) {
          this.ticketToLink = s.item.id
          this.searchForTickets = ''
          this.$nextTick(function () {
            this.searchForTickets = s.item!.key + ' ' + s.item!.title
            this.refreshAutosuggestKey = Math.floor(Math.random() * 100)
            this.hasUnsavedTicketEdits = true
          })
        } else {
          this.$nextTick(function () {
            this.searchForTickets = ''
            this.ticketToLink = ''
            this.ticketsForLink = []
          })
        }
      }
    },
    removeWatcher() {
      if (!this.ticket.watchers) {
        return
      }
      const index = this.ticket.watchers.findIndex(c => c.userId === this.$store.getters['user/getUserId'])
      if (index === -1) {
        return
      }
      Vue.delete(this.ticket.watchers, index)
      this.persistWatchers()
    },
    addWatcher() {
      if (!this.ticket.watchers) {
        this.$set(this.ticket, 'watchers', [])
      }
      const employee = this.employeeList.find(x => x.id === this.userId)
      this.ticket.watchers.push({
        accountId: this.accountId,
        userId: this.userId,
        name: employee?.profile ? `${employee.profile.firstName} ${employee.profile.lastName}` : this.fullName,
        profilePictureUrl: employee?.profile?.pictureUrl || this.userProfilePicture
      })
      this.persistWatchers()
    },
    persistWatchers() {
      if (this.orderTicket !== null) {
        this.$emit('watchers-changed', this.ticket.watchers)
        Vue.toasted.show(this.$t('message.successMessages.ticket'), { type: 'success' })
        return
      }
      this.updateTicket('watcher')
    },
    mountedReference(references) {
      const refs = []

      // Skip loading of references if we're not logged in (public page)
      if (!this.accountId) {
        return refs
      }

      for (let index = 0; index < references.length; index++) {
        refs.push({
          object: null,
          building: null,
          property: null,
          condominium: null,
          tenancy: null,
          device: null,
          contact: null,
          request: null,
          room: null,
          mortgage: null,
          insurance: null,
          listContact: [],
          relatedReference: []
        })
        const element = references[index].id
        if (element.startsWith('condominium') || element.startsWith('tenancy')) {
          if (element.startsWith('condominium')) {
            ObjectApi.getCondominiumByIds([element])
              .then(response => {
                refs[index].condominium = response.condominiums[0]
                if (refs[index] && refs[index].condominium) {
                  if (refs[index].condominium.owner.contactIds.length > 0) {
                    ContactApi.contactResolve(refs[index].condominium.owner.contactIds)
                      .then(responseCon => {
                        refs[index].listContact = [...responseCon.persons, ...responseCon.companies] as Contact[]
                      })
                  }
                  ObjectApi.getObject(refs[index].condominium.objectId)
                    .then(responseObj => {
                      refs[index].object = responseObj.object
                      ObjectApi.getBuildingByObjectId(refs[index].object.id).then(responseBuild => {
                        refs[index].building = responseBuild.building
                        ObjectApi.getPropertiesByBuildingIds([refs[index].building.id])
                          .then(responseProp => {
                            refs[index].property = responseProp.properties[0]
                          })
                      }).catch(e => {
                        console.log(e)
                        Vue.toasted.show(this.$t('message.loadingErrors.advertisement'), { type: 'error' })
                      })
                    })
                }
              })
          } else if (element.startsWith('tenancy')) {
            ObjectApi.getTenanciesByIds([element])
              .then(response => {
                const tenancy = response.tenancies?.[0]
                if (!tenancy) {
                  return
                }
                refs[index].tenancy = tenancy
                if (tenancy.tenant?.contactIds?.length > 0) {
                  ContactApi.contactResolve(tenancy.tenant.contactIds)
                    .then(responseCon => {
                      refs[index].listContact = [...responseCon.persons, ...responseCon.companies] as Contact[]
                    })
                }
                ObjectApi.getObject(tenancy.objectId)
                  .then(response => {
                    refs[index].object = response.object
                    ObjectApi.getBuildingByObjectId(refs[index].object.id).then(response => {
                      refs[index].building = response.building
                      ObjectApi.getPropertiesByBuildingIds([refs[index].building.id])
                        .then(response => {
                          refs[index].property = response.properties[0]
                        })
                    }).catch(e => {
                      console.log(e)
                      Vue.toasted.show(this.$t('message.loadingErrors.advertisement'), { type: 'error' })
                    })
                  })
              })
          }
        }
        if (element.startsWith('request')) {
          RequestApi.listByIds([element])
            .then(response => {
              if (response.requests.length > 0) {
                this.references[index].request = response.requests[0]
                if (response.requests[0].requesters.length > 0) {
                  const cont = response.requests[0].requesters[0].contactId
                  if (cont) {
                    ContactApi.contactResolve([cont])
                      .then(response => {
                        this.references[index].listContact = ([...response.persons, ...response.companies] as Contact[]).filter(c => cont.includes(c.id))
                      })
                  }
                }
                if (this.references[index].request) {
                  ObjectApi.getObject(this.references[index].request.objectId)
                    .then(response => {
                      this.references[index].object = response.object
                      ObjectApi.getBuildingByObjectId(this.references[index].object.id).then(response => {
                        this.references[index].building = response.building
                        ObjectApi.getPropertiesByBuildingIds([this.references[index].building.id])
                          .then(response => {
                            this.references[index].property = response.properties[0]
                          })
                      }).catch(e => {
                        console.log(e)
                        Vue.toasted.show(this.$t('message.loadingErrors.advertisement'), { type: 'error' })
                      })
                    })
                }
              }
            })
        }
        if (element.startsWith('obj')) {
          ObjectApi.getObject(element)
            .then(response => {
              refs[index].object = response.object
              ObjectApi.getBuildingByObjectId(refs[index].object.id).then(response => {
                refs[index].building = response.building
                ObjectApi.getPropertiesByBuildingIds([refs[index].building.id])
                  .then(response => {
                    refs[index].property = response.properties[0]
                  })
              }).catch(e => {
                console.log(e)
                Vue.toasted.show(this.$t('message.loadingErrors.advertisement'), { type: 'error' })
              })
            })
        }
        if (element.startsWith('room')) {
          RoomApi.listById([element]).then(roomResponse => {
            const room = roomResponse.rooms[0]
            refs[index].room = room
            if (room && room.referenceId.startsWith('obj')) {
              ObjectApi.getObject(room.referenceId)
                .then(response => {
                  refs[index].object = response.object
                  ObjectApi.getBuildingByObjectId(refs[index].object.id).then(response => {
                    refs[index].building = response.building
                    ObjectApi.getPropertiesByBuildingIds([refs[index].building.id])
                      .then(response => {
                        refs[index].property = response.properties[0]
                      })
                  }).catch(e => {
                    console.log(e)
                    Vue.toasted.show(this.$t('message.loadingErrors.advertisement'), { type: 'error' })
                  })
                })
            }
            if (room && room.referenceId.startsWith('building')) {
              ObjectApi.getBuilding(room.referenceId).then(response => {
                refs[index].building = response.building
                ObjectApi.getPropertiesByBuildingIds([refs[index].building.id])
                  .then(response => {
                    refs[index].property = response.properties[0]
                  })
              }).catch(e => {
                console.log(e)
                Vue.toasted.show(this.$t('message.loadingErrors.advertisement'), { type: 'error' })
              })
            }
          })
        }
        if (element.startsWith('building')) {
          ObjectApi.getBuilding(element).then(response => {
            refs[index].building = response.building
            ObjectApi.getPropertiesByBuildingIds([element])
              .then(response => {
                refs[index].property = response.properties[0]
              })
          })
        }
        if (element.startsWith('prop')) {
          ObjectApi.getPropertyListByIds([element])
            .then(response => {
              refs[index].property = response.properties[0]
            })
        }
        if (element.startsWith('contact')) {
          ContactApi.contactResolve([element])
            .then(response => {
              refs[index].contact = ([...response.persons, ...response.companies] as Contact[]).find(x => x.id === element)
            })
        }
        if (element.startsWith('device')) {
          ObjectApi.listDevicesByIds([element])
            .then(response => {
              if (response.devices.length > 0) {
                const device = response.devices[0]
                refs[index].device = device
                if (device.roomId) {
                  RoomApi.listById([device.roomId]).then(roomResponse => {
                    if (roomResponse.rooms.length > 0) {
                      refs[index].room = roomResponse.rooms[0]
                    }
                  })
                }
                if (device.referenceId?.startsWith('obj')) {
                  ObjectApi.getObject(device.referenceId)
                    .then(response1 => {
                      refs[index].object = response1.object
                      refs[index].relatedReference.push(response1.object)
                      RoomApi.getRoomsByReferenceIds([response1.object.id]).then(response2 => {
                        if (response2.rooms.length > 0) {
                          refs[index].relatedReference.push(response2.rooms.find(x => x.id === device.roomId))
                        }
                      })
                      ObjectApi.getBuildingByObjectId(refs[index].object.id).then(response => {
                        refs[index].building = response.building
                        ObjectApi.getPropertiesByBuildingIds([refs[index].building.id])
                          .then(response => {
                            refs[index].property = response.properties[0]
                          })
                      }).catch(e => {
                        console.log(e)
                        Vue.toasted.show(this.$t('message.loadingErrors.advertisement'), { type: 'error' })
                      })
                    })
                }
                if (device.referenceId?.startsWith('building')) {
                  ObjectApi.getBuilding(device.referenceId)
                    .then(response => {
                      refs[index].building = response.building
                      refs[index].relatedReference.push(response.building)
                      ObjectApi.getPropertiesByBuildingIds([response.building.id])
                        .then(response => {
                          refs[index].property = response.properties[0]
                        })
                    })
                }
                if (device.referenceId?.startsWith('prop')) {
                  ObjectApi.getPropertyListByIds([device.referenceId])
                    .then(response => {
                      refs[index].property = response.properties[0]
                      refs[index].relatedReference.push(response.properties[0])
                    })
                }
              }
            })
        }
        if (element.startsWith('mortgage')) {
          ObjectApi.getMortgagesByIds([element])
            .then(response => {
              if (response.mortgages.length > 0) {
                refs[index].mortgage = response.mortgages[0]
                if (response.mortgages[0].referenceId?.startsWith('building')) {
                  ObjectApi.getBuilding(response.mortgages[0].referenceId)
                    .then(buildingResponse => {
                      refs[index].building = buildingResponse.building
                      ObjectApi.getPropertiesByBuildingIds([buildingResponse.building.id])
                        .then(propertResponse => {
                          refs[index].property = propertResponse.properties[0]
                        })
                    })
                }
                if (response.mortgages[0].referenceId?.startsWith('prop')) {
                  ObjectApi.getPropertyListByIds([response.mortgages[0].referenceId])
                    .then(propertyResponse => {
                      refs[index].property = propertyResponse.properties[0]
                    })
                }
              }
            })
        }
        if (element.startsWith('ins')) {
          ObjectApi.getInsurancesByIds([element])
            .then(response => {
              if (response.insurances.length > 0) {
                refs[index].insurance = response.insurances[0]
                if (response.insurances[0].referenceId?.startsWith('building')) {
                  ObjectApi.getBuilding(response.insurances[0].referenceId)
                    .then(buildingResponse => {
                      refs[index].building = buildingResponse.building
                      ObjectApi.getPropertiesByBuildingIds([buildingResponse.building.id])
                        .then(propertResponse => {
                          refs[index].property = propertResponse.properties[0]
                        })
                    })
                }
                if (response.insurances[0].referenceId?.startsWith('prop')) {
                  ObjectApi.getPropertyListByIds([response.insurances[0].referenceId])
                    .then(propertyResponse => {
                      refs[index].property = propertyResponse.properties[0]
                    })
                }
              }
            })
        }
      }
      return refs
    },
    replyMail(email) {
      this.prepareReplyMail(email)
    },
    replyAllMail(email) {
      this.prepareReplyAll(email)
    },
    forwardMail(email) {
      this.prepareForwardMail(email)
    },
    printMessage(shortContent) {
      this.shortContent = shortContent
    },
    refreshList() {
      if (this.ticket?.id && this.ticket.id !== '') {
        this.loadMails([this.ticket.id])
      }
    },
    showDetail(mail) {
      this.dynamicId = Math.random().toString(36).substr(2, 9)
      this.selectedMail = mail
      this.setMailDetailMailObject(mail)
      this.$nextTick(function () {
        this.callShowDetailMailModal()
      })
    },
    forwardAsAttachment(mediaList, email) {
      this.setSubject('Fwd: ' + email.subject)
      this.setSendMailHasContext(true)
      this.setSendMailContacts([])
      this.setEmailToForwardAsAttachment(mediaList)
      this.$nextTick(function () {
        if (this.getMailboxesForUserId(this.userId).length === 0) {
          this.$nextTick(function () {
            Vue.toasted.show(this.$t('message.settings.mailbox.configMailboxToast'), { type: 'error' })
          })
        } else {
          this.callShowSendMailModal()
        }
      })
    },
    unlinkTicket(email) {
      MessageApi.setReferenceIds([email.id], email.referenceIds.filter(x => x !== this.ticket.id))
        .then(() => {
          Vue.toasted.show(this.$t('message.successMessages.emailUnLinked'), { type: 'success' })
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.savingErrors.reference'), { type: 'error' })
        })
        .finally(() => {
          this.loadMails([this.ticket.id])
          this.$bvModal.hide('confirmationModalUnlinkTicket' + email ? email.id : '')
        })
    },
    discardChanges() {
      if (this.$refs.confirmationModal) {
        this.$refs.confirmationModal.hide()
      }
      this.hasUnsavedTicketEdits = false
      if (this.nextPath !== null) {
        this.nextPath()
      } else {
        this.$router.go(-1)
      }
    },
    openChangeConditionModal() {
      this.$bvModal.show('changeConditionModal')
    },
    updateDevicesCondition() {
      const promises = this.referencesDevices.map(device => {
        return ObjectApi.updateDevice(device)
      })

      Promise.all(promises)
        .then(() => {
          this.referencesDevices.forEach(updatedDevice => {
            const reference = this.references.find(ref => ref.device?.id === updatedDevice.id)
            if (reference) {
              reference.device.condition = updatedDevice.condition
              reference.device.note = updatedDevice.note
            }
          })
          Vue.toasted.show(this.$t('message.successMessages.dataSaved'), { type: 'success' })
          this.$bvModal.hide('changeConditionModal')
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.savingErrors.data'), { type: 'error' })
        })
    },
    openChangeDeviceModal() {
      this.mainDevice = JSON.parse(JSON.stringify(this.referencesDevices[0]))
      const input = this.$options.filters.objectToDateInput(this.mainDevice.uninstallation)
      this.mainDeviceUninstallDateInput = typeof input === 'string' ? input : ''
      this.$nextTick(() => {
        this.$bvModal.show('ChangeDeviceModal')
      })
    },
    cancelChangeDevice() {
      this.$bvModal.hide('ChangeDeviceModal')
      this.$v.mainDeviceUninstallDateInput?.$reset()
    },
    saveUninstallDate() {
      this.$v.mainDeviceUninstallDateInput.$touch()
      if (this.$v.mainDeviceUninstallDateInput.$invalid) {
        if (this.$v.mainDeviceUninstallDateInput.moreThanInstallationDate === false) {
          Vue.toasted.show(this.$t('message.savingErrors.uninstallation'), { type: 'error' })
        }
        return
      }

      const uninstallDate = this.toObject(this.mainDeviceUninstallDateInput)

      if (this.mainDevice && uninstallDate) {
        ObjectApi.updateDevice({ ...this.mainDevice, uninstallation: uninstallDate })
          .then(() => {
            const referenceWithDevice = this.references.find(ref =>
              ref.device?.id === this.mainDevice.id
            )
            if (referenceWithDevice) {
              referenceWithDevice.device.uninstallation = uninstallDate
            }
            this.$bvModal.hide('ChangeDeviceModal')

            this.prepareNewDeviceModal(this.mainDevice, uninstallDate)
          })
          .catch(e => {
            console.log(e)
            Vue.toasted.show(this.$t('message.savingErrors.data'), { type: 'error' })
          })
      }
    },
    newDeviceUpdated(updatedDevice) {
      this.newDevice = updatedDevice
    },
    prepareNewDeviceModal(oldDevice, installationDate) {
      this.newDevice = JSON.parse(JSON.stringify(helper.emptyDevice))
      this.newDevice.type = oldDevice.type
      this.newDevice.ownerId = oldDevice.ownerId
      this.newDevice.referenceId = oldDevice.referenceId
      this.newDevice.roomId = oldDevice.roomId
      this.newDevice.installation = installationDate ?? this.toObject(oldDevice.uninstallation)

      this.$nextTick(() => {
        this.$bvModal.show('newDeviceModal')
      })
    },
    cancelReplaceDevice() {
      this.$bvModal.hide('newDeviceModal')
    },
    saveNewDevice() {
      ObjectApi.createDevice(this.newDevice)
        .then((response) => {
          this.$bvModal.hide('newDeviceModal')
          this.newDevice = null

          Vue.toasted.show(this.$t('message.successMessages.dataSaved'), { type: 'success' })
        })
        .catch(e => {
          console.log(e)
          Vue.toasted.show(this.$t('message.savingErrors.data'), { type: 'error' })
        })
    }
  },
  validations() {
    const validation: {
      unregisteredName?: {
        required: typeof required
        moreThanTwoChar: typeof moreThanTwoChar
      }
      mainDeviceUninstallDateInput?: {
        required: typeof required
        moreThanInstallationDate: typeof moreThanInstallationDate
      }
    } = {}
    if (this.publicTicket) {
      validation.unregisteredName = {
        required,
        moreThanTwoChar
      }
    }
    if (this.mainDevice) {
      validation.mainDeviceUninstallDateInput = {
        required,
        moreThanInstallationDate
      }
    }
    return validation
  }
})
</script>

<style lang="scss" scoped>
.checklist-row {
  border: 0;

  .status-design {
    border: 0;
    option {
      background-color: white;
    }
    &.open {
      background-color: lightgrey;
    }
    &.in_progress {
      background-color: lightskyblue;
    }
    &.done {
      background-color: darkseagreen;
    }
  }

  .checklist-status-btn {
    visibility: hidden;
  }
}
.linked-row {
  border-radius: 0.2rem;
  background-color: white;
  border: 1px solid $color-input-border;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out, -webkit-box-shadow 0.15s ease-in-out;

  .label {
    line-height: 1.8rem;
  }

  .status-design {
    min-width: 125px !important;
    text-align: center;
    padding: 0.35rem 0.5rem;
    text-transform: uppercase;
    font-size: small;
    border-radius: 0.2rem;

    &.open {
      background: lightgrey;
    }

    &.in_progress {
      background: lightskyblue;
    }

    &.done {
      background: darkseagreen;
    }
  }

  .remove-linked-btn {
    visibility: hidden;
  }
}

.linked-row:hover {
  .remove-linked-btn {
    visibility: visible !important;
  }
}
//
//:deep(.status-design) {
//  text-transform: uppercase;
//  font-size: small;
//  width: auto;
//  border-radius: 0.2rem;
//
//  option {
//    background-color: #FFF;
//  }
//
//  &.open {
//    background-color: lightgrey;
//  }
//
//  &.in_progress {
//    background-color: lightskyblue;
//  }
//
//  &.done {
//    background-color: darkseagreen;
//  }
//}

:deep(.icon-dropdown) {
  padding: 0 0.5rem;
  -webkit-box-shadow: none !important;
  box-shadow: none !important;
  text-decoration: none !important;
  background-color: transparent;

  &:hover, &:active {
    background-color: transparent;
  }
}

.watcher-element {
  :deep(.dropdown-menu-right) {
    width: 14rem;

    .dropdown-item, .b-dropdown-text {
      background-color: transparent;
      padding: 0.25rem 0.5rem;
      color: initial !important;
      text-decoration: none !important;
    }
  }
}

.uploaded-file.image {
  background-position: center center;
  background-size: contain;
  background-repeat: no-repeat;
}

.uploaded-file {
  background-position: center center;
  background-size: cover;
}

.subtitle {
  color: #495057;
  font-weight: 400;
}

.z-index3 {
  z-index: 3;
}

.pos_abs {
  position: absolute;
  right: 20px;
  width: auto;
  text-align: right;
  z-index: 2;
}

.media-block {
  padding: 0.5rem;
  padding-left: 0;
}

@media (min-width: 768px) {
  .uploaded-file {
    max-height: 100px;
    min-height: 4em;
  }
}

@media (min-width: 992px) {
  .uploaded-file {
    max-height: 110px;
    min-height: 5em;
  }
}

@media (min-width: 1200px) {
  .uploaded-file {
    max-height: 120px;
    min-height: 5.5em;
  }
}

@media (min-width: 1600px) {
  .uploaded-file {
    max-height: 130px;
    min-height: 7em;
  }
}

.borderTop {
  border-top: 1px solid #ced4da;
  padding-top: 5px;
}

.title-card {
  color: #000;
}

:deep(.hrefSelector .nav-item a:hover) {
  text-decoration: none !important;
  cursor: default !important;
}

:deep(.hrefSelector .nav-item a .text-blue) {
  font-size: 0.875rem;
}

.disableOptionHover {
  cursor: default !important;
  color: #6c757d !important;
  opacity: 0.5;
}

.color-box.condo_owner {
  background: #f589c7;
}

.color-box.condominium {
  background: #913489;
}

.color-box.tenancy {
  background: #00a4bd;
}

.color-box.object {
  background: #f38d38;
}

.color-box.device {
  background: #767171;
}

.color-box.request {
  background: #f7b6ae;
}

.containerCircles {
  height: 21px;
  align-items: center;
  display: flex;
  align-self: baseline;
}

.connection a.router-link-active, .connection a {
  color: inherit !important;
  text-decoration: none !important;
}

.connection a.router-link-active:hover, .connection a:hover {
  color: #0055b7 !important;
  text-decoration: underline !important;
  cursor: pointer;
}

.logo_company {
  height: 40px;
}

:deep(.autosuggest__results-item.autosuggest__results-item--highlighted) {
  background-color: rgba(0, 0, 0, 0.03);
}

.text-lg {
  font-size: 2.1875rem;
}

:deep(.custom-spin .spinner-border) {
  width: 16px !important;
  height: 16px !important;
}

.iconFile {
  max-width: 45px;
}

:deep(.collapse-title) {
  &.collapsed {
    h5 {
      background-image: url('../../../assets/arrow_down.svg');
    }
  }

  h5 {
    background-image: url('../../../assets/arrow_up.svg');
    background-repeat: no-repeat;
    font-size: large;
    font-weight: 500 !important;
    height: 1.2rem;
    margin-top: 0.2rem;
    margin-bottom: 0.2rem;
    background-position: right;
  }
}

.iconsStatusUpdate {
  width: 20px;
}

.order-actions {
  flex-wrap: wrap;
  justify-content: flex-end;
}

.active-watcher {
  color: #1398e3;
}

.length-watchers {
  color: #000;

  &.active {
    color: #1398e3;
  }
}

.link-icon {
  line-height: 2 !important;
}

.block {
  cursor: pointer;

  .only-on-hover {
    visibility: hidden;
    line-height: 0;
  }

  &:hover .only-on-hover {
    visibility: visible;
  }
}

.text-gray {
  color: $color-text-grey-light;
}

.item-task {
  padding: 10px 0;
  margin-bottom: 0.25rem;
  margin-top: 0.25rem;
  border: 1px solid #ddd;
  border-radius: 3px;
  background-color: #fdfdfd;

  p, .input-task {
    margin: 0;
    width: 95%;
  }

  .task-assignee {
    font-size: 0.9rem;
    padding: 0.25rem 0.5rem;
    border-radius: 3px;
  }

  .task-status-design {
    cursor: pointer;
    font-size: 14px;
    line-height: 16px;
    height: 31px;
    width: 100%;
    box-shadow: none !important;
    color: #333 !important;
    background-color: #dfdfdf !important;
    border-color: #dfdfdf !important;
  }
}

.item-task:first-child {
  margin-top: 1rem;
}

.container_textArea {
  position: relative;
  display: flex;

  .icon {
    position: relative;
    height: 30px;
    width: 35px;
    margin-left: 10px;
  }
}
</style>
